// Cleanup Classes
import {
  buildSelectionTextStats,
  buildStoryVideoContext,
} from '@src/analytics/eventPayloadHelpers';
import {
  trackReplaceTranscriptText,
  trackSaveClipFromTranscript,
  trackSavePullQuoteFromTranscript,
  type TranscriptActionSource,
} from '@src/analytics/EventTracker';
import SavedSegmentsFilterDropdown from '@src/components/common/SavedSegmentsFilterDropdown';
import { type TopicFilterMode } from '@src/components/common/TopicFilterDropdown';
import { stripClipPreviewParams } from '@src/components/content-components/clipPreviewUrlState';
import { useCompanyDictionary } from '@src/hooks/useCompanyDictionary';
import { useDisplayTranscriptElements } from '@src/hooks/useDisplayTranscriptElements';
import { useHighlightHoverPopover } from '@src/hooks/useHighlightHoverPopover';
import { useKaraoke } from '@src/hooks/useKaraoke';
import { useOverlayRangeAdjust } from '@src/hooks/useOverlayRangeAdjust';
import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import {
  isElementReassigned,
  useSpeakerOverrides,
} from '@src/hooks/useSpeakerOverrides';
import { getSpeakerColor as getSpeakerColorFromDatoId } from '@src/services/SpeakerManagementService';
import {
  cacheAndWriteSpeakerAssignment,
  injectSpeakerLabel,
  notifySpeakerAddedToStory,
  notifySpeakerCacheUpdated,
} from '@src/stores-v2/speakerCache';
import {
  useDatoClient,
  useEditor,
  useKaraokeStore,
  useTranscription,
  useTranslationStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import { analytics } from '@src/utility/analytics';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { LogManager } from '@src/utility/Logger';
import { clampAnchorToSelectionRun } from '@src/utility/placementAnchor';
import {
  DEFAULT_SAVED_SEGMENT_COLOR,
  savedSegmentDefaultName,
} from '@src/utility/savedSegmentColors';
import {
  clearSpeakerCache as clearSpeakerUtilsCache,
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';
import { forIPad } from '@src/utility/styled';
import {
  dispatchTranscriptSeek,
  seekToInitialIndex,
} from '@src/utility/transcriptEvents';
import {
  applyActiveOverlayBrighten,
  applyClipHighlights,
  applyOverlayHighlights,
  applyQuoteHighlights,
  brightenOverlayUnderlinesById,
  clearActiveOverlayBrighten,
  clearClipHighlights,
  clearOverlayHighlights,
  clearQuoteHighlights,
  findClipRanges,
  findFirstVisibleRangeIndex,
  findOverlayRanges,
  findQuoteRanges,
  findSavedSegmentRanges,
  findTranslatedOverlayRanges,
  type HighlightRange,
  highlightRangesToPlaybackRanges,
  OVERLAY_MATCH_ACTIVE_CLASS,
  OVERLAY_MATCH_CLASS,
  rangesToIndices,
  resolveActiveOverlayRangeIds,
} from '@src/utility/transcriptQuoteUtils';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useTranscriptOperations } from '../../hooks/useTranscriptOperations';
import ReplaceIcon from '../../svgs/ReplaceIcon';
import {
  ContentTab,
  type ImageKey,
  type ImageWithType,
  Studio,
  type TranscriptContextMenuActions,
  type TranscriptContextMenuDisabledReasons,
} from '../../types.ts/general';
import { TranscriptionStatus } from '../../types.ts/video';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import Modal from '../common/Modal';
import PhotoModalTop from '../common/PhotoModalTop';
import PhotoModal from '../common/PhotosModal';
import { PickerModalCard } from '../common/pickerModalStyles';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { UndoRedoButtons } from '../common/UndoRedoButtons';
import DebugModal from '../debug/DebugModal';
import AssignSpeakerModal from '../sidepanel/modals/AssignSpeakerModal';
import { TranscriptionDiff as TranscriptionDiffNew } from './_TranscriptionDiff';
import {
  mergeCaptionRows,
  resolveCaptionBreakHandlers,
} from './captionBreakEditing';
import {
  type HeaderExtrasSlots,
  isHeaderExtrasSlots,
  resolveFilters,
} from './headerExtrasSlots';
import { HighlightTogglePill } from './HighlightTogglePill';
import { applyContextMenuSurfaceGates } from './hooks/contextMenuSurfaceGates';
import { useEditSpeaker } from './hooks/useEditSpeaker';
import { useRedlineToggle } from './hooks/useRedlineToggle';
import { useRedlineUrlState } from './hooks/useRedlineUrlState';
import { useSavedSegmentVisibility } from './hooks/useSavedSegmentVisibility';
import { useSavedSegmentWash } from './hooks/useSavedSegmentWash';
import { overlayShowsTranscriptUnderline } from './hooks/useTimelineMarkers';
import { useTranscriptIndexJump } from './hooks/useTranscriptIndexJump';
import { useTranscriptOverflowItems } from './hooks/useTranscriptOverflowItems';
import { useTranslatedCaptionLines } from './hooks/useTranslatedCaptionLines';
import { useTranslatedContextMenuActions } from './hooks/useTranslatedContextMenuActions';
import { buildTranslatedPhraseTimeInfos } from './hooks/useTranslatedRender';
import { KaraokeLinesView } from './KaraokeLinesView';
import { OverlayRangeHandles } from './OverlayRangeHandles';
import { PanelButton } from './panelButtonStyles';
import { RedlineToggleButton } from './RedlineToggleButton';
import { RegenerateTranscriptionModal } from './RegenerateTranscriptionModal';
import SpeakerFormModal from './SpeakerFormModal';
import { reservesTopicGutter } from './topicRailStyles';
import { TranscriptContextMenu } from './TranscriptContextMenu';
import { TranscriptionDiffTranslated } from './TranscriptionDiffTranslated';
import { TranscriptionText } from './TranscriptionText';
import { TranscriptionTextTranslated } from './TranscriptionTextTranslated';
import { Header, HeaderTail, ScrollableText } from './transcriptLayoutStyles';
import { TranscriptModeToggle } from './TranscriptModeToggle';
import TranscriptOverflowMenu from './TranscriptOverflowMenu';
import TranscriptSearch from './TranscriptSearch';
import TranscriptSpeakerContextMenu from './TranscriptSpeakerContextMenu';
import { TranslatedCaptionsView } from './TranslatedCaptionsView';
import { buildPhraseStepIndices } from './translatedWordAtoms';
import {
  bypassScrollBlocker,
  computeLineHeightPx,
  computeLockRestoreDecision,
  computePlayheadScrollTop,
  CURSOR_TOP_OFFSET_LINES,
  extractTranscriptSelectionText,
  findByForwardInitialIndexScan,
  findVisibleByDataIndex,
  findVisibleCursorElement,
  findVisibleDiffBlockForIndex,
  firstSentencePreview,
  getTranscriptElementBlocks,
  groupBlocksByParagraph,
  isFocusHeldElsewhere,
  REQUEST_CURSOR_Y_LOCK_EVENT,
  resolveBlockElementAtPoint,
} from './utils';
import { WordReplacementModal } from './WordReplacementModal';

import type { ReviewHandoffProp } from '@src/components/aiVoices/reviewHandoff';
import type { ClipTimelineMarkers } from './hooks/useTimelineMarkers';
import type { MarkerClickHandler } from './TimelineMarkerChip';
import type { SpeakerClickInfo } from './TranscriptionText';
import type { TranscriptElementBlock } from './utils';
import type { KaraokeLine } from '@src/editor/types/karaoke';
import type { HighlightHoverInfo } from '@src/hooks/useHighlightHoverPopover';
import type { Result } from '@src/services/common/Result';
import type {
  FetchSpeakersResult,
  SpeakerData,
} from '@src/services/SpeakerManagementService';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptElementWithViewData } from '@src/types.ts/transcript';
import type { MouseEvent } from 'react';

const logger = LogManager.getLogger('component:Transcript');

/**
 * Minimum delta (in seconds) between successive `videoCreator.time` ticks
 * before the cursor-recalc effect will re-derive the cursor position. The
 * renderer fires time updates at ~60Hz; deltas below 100ms are below the
 * threshold of perceived cursor movement and re-running the recalc on
 * every tick saturates the React batcher during playback.
 */
const CURSOR_RECALC_MIN_DELTA_SEC = 0.1;

/**
 * Cursor lookahead used by the auto-scroll target index. Scroll lands ahead
 * of the playhead so the next several words are already visible. Smaller at
 * the start of the transcript to avoid scrolling past the top.
 */
const CURSOR_LOOKAHEAD_LATE = 10;
const CURSOR_LOOKAHEAD_EARLY = 1;
const CURSOR_LOOKAHEAD_EARLY_THRESHOLD = 10;

// Narrow service interfaces for dependency injection
export interface TranscriptSaveService {
  save(asFinal: boolean): Promise<Result<{ republishRequired: boolean }>>;
}

export interface TranscriptPullQuoteService {
  savePullQuote(input: {
    storyId: string;
    story: any;
    selectedText: string;
    startIndex: number;
    endIndex: number;
    transcriptElements: TranscriptElementWithViewData[];
    editorName?: string;
  }): Promise<Result<any>>;
}

export interface TranscriptClipSaveService {
  saveClip(input: {
    startIndex: number;
    endIndex: number;
    relevantElements?: TranscriptElementWithViewData[];
  }): Promise<{ id: string; title: string } | null>;
}

/**
 * Save-for-later → combine flow: persist named transcript segments on the
 * story (rendered as inline highlights), create a single clip from one, or
 * combine several into one compilation reel. Injected per surface so the
 * shared Transcript never imports store/repository code directly.
 */
export interface TranscriptSavedSegmentService {
  /**
   * Create a new named segment group seeded with one range; returns the
   * group's key (epoch ms) or null on failure.
   */
  saveSegment(input: {
    name: string;
    startIndex: number;
    endIndex: number;
    color?: string;
  }): Promise<number | null>;
  /** Append the current selection to an existing segment group. */
  addRangeToSegment(input: {
    key: string;
    startIndex: number;
    endIndex: number;
  }): Promise<void>;
  /** Rename a saved segment group. */
  renameSegment(key: string, name: string): Promise<void>;
  /** Delete a saved segment group by its key. */
  removeSegment(key: string): Promise<void>;
  /** Create one clip from a single-range segment group. */
  createClipFromSegment(
    key: string,
  ): Promise<{ id: string; title: string } | null>;
  /** Combine one segment group's ranges into a compilation reel. */
  combineSegment(key: string): Promise<{ id: string; title: string } | null>;
}

export interface TranscriptMediaService {
  addPhotoToTimeline(input: {
    item: {
      id: string;
      src: string;
      description: string;
      aspectRatio: number;
      alt: string;
      type: 'artifact' | 'stock' | 'ai';
    };
    time: number;
    duration: number;
    track: number;
    useComposition: boolean;
    id: string;
  }): Promise<void>;
}

type TranscriptProps = {
  videoCreator: ITranscriptVideoCreator;
  /**
   * PLAT-10272 (T12) — opt-in: enable the inline "Fix Translation" span-replace
   * affordance in the translated prose view (`TranscriptionTextTranslated`). Off
   * by default so only the Speakers & Transcripts host turns it on; other hosts
   * (Creator Studio, clip preview, captions) are unchanged (epic D9).
   */
  enableInlineSpanReplace?: boolean;
  /**
   * PLAT-10616 — re-dub the selected span. Forwarded to the translated prose
   * view's inline span-replace layer; when set, the selection menu offers
   * "Re-dub AI Voice" and the host opens the selection re-dub popover beside
   * it (PLAT-12022). Only the Speakers & Transcripts host wires it.
   */
  reviewHandoff?: ReviewHandoffProp;
  /**
   * PLAT-10265 — opt-in: the changed-phrase treatment (green "edited" mark +
   * hover "Original translation" tooltip with one-click Undo) in the regular
   * translated prose view (`TranscriptionTextTranslated`). Off by default; only
   * the Speakers & Transcripts and clip-preview hosts turn it on. Not forwarded
   * to the redline/diff view (out of scope).
   */
  enableChangedHighlight?: boolean;
  saveService?: TranscriptSaveService;
  savePullQuoteService?: TranscriptPullQuoteService;
  clipSaveService?: TranscriptClipSaveService;
  savedSegmentService?: TranscriptSavedSegmentService;
  /**
   * Invoked by the "Combine into Clip" button shown next to the highlight
   * match nav when exactly one multi-range highlight group is filtered via
   * the Highlights dropdown. The host owns the combine flow (toast,
   * analytics, open-clip deep link), mirroring HighlightActionPopover's
   * combine action. Omitted ⇒ the button never renders.
   */
  onCombineSegment?: (key: string) => Promise<void>;
  /**
   * PLAT-10949 — reports the time windows the host's video player should
   * limit playback to while the Highlights filter is in Show Only mode: the
   * active highlight ranges as sorted, merged `[start, end]` seconds
   * (story-absolute). `null` whenever highlight-only playback should not
   * apply (Highlight mode, highlights hidden, or no active ranges). Hosts
   * thread the value into their preview player's `playbackSegments` so
   * playing the video skips the non-highlighted gaps.
   */
  onHighlightPlaybackRangesChange?: (
    ranges: { start: number; end: number }[] | null,
  ) => void;
  mediaService?: TranscriptMediaService;
  hideHeader?: boolean;
  hideCaptions?: boolean;
  hideAddPhoto?: boolean;
  /**
   * PLAT-10105 — hide the "Add Overlay Graphic" context-menu action. The
   * action is also dropped whenever `onAddOverlayGraphicRequested` is not
   * provided, so surfaces that don't support overlay insertion never show
   * it regardless of this flag. Both surfaces pass the negation of the single
   * `enableTranscriptOverlayAdd` flag (PLAT-10213 unified the former
   * per-surface `creator-/content-studio-transcript-add-overlay-graphic`).
   */
  hideAddOverlayGraphic?: boolean;
  /**
   * PLAT-10105 — invoked when the user picks "Add Overlay Graphic" from the
   * context menu on a transcript selection. Receives the selection's
   * resolved timeline range + text so the caller (ClipTranscriptBridge) can
   * open the filtered overlay picker and insert the chosen overlay bound to
   * `[startTime, endTime]`. Kept as a callback so the shared Transcript never
   * imports composition/graphics-specific picker UI.
   */
  onAddOverlayGraphicRequested?: (range: {
    startTime: number;
    endTime: number;
    text: string;
    /**
     * Stable source `initial_index` of the first/last selected word, stored on
     * the overlay so its transcript underline maps to these words through later
     * edits (see `findOverlayRanges`).
     */
    anchorStartInitialIndex?: number;
    anchorEndInitialIndex?: number;
  }) => void;
  hideCutText?: boolean;
  hideRemoveText?: boolean;
  hideReplace?: boolean;
  hideSaveAsClip?: boolean;
  showUndoRedo?: boolean;
  /** When true, show the QUOTES highlight toggle button */
  showQuotesToggle?: boolean;
  /** When true, show the CLIPS highlight toggle button */
  showClipsToggle?: boolean;
  /** When true, display the original (full) transcript instead of clip-modified elements */
  useOriginalTranscript?: boolean;
  /**
   * When set, renders the transcript as a translation (read-only) in the
   * given BCP 47 language. Null/undefined keeps the original editor view.
   */
  language?: string | null;
  /**
   * Header slot. A plain node renders in the right-pinned tail; pass
   * `{ filters, trailing }` to also place host filters with the content
   * filter group — see `headerExtrasSlots.ts`.
   */
  headerExtras?: React.ReactNode | HeaderExtrasSlots;
  /**
   * Slot rendered as its own row directly under the header, above the
   * scrollable transcript body. Use for content that needs to break out of
   * the header's single-row layout (e.g. wrap-friendly chip lists).
   */
  belowHeader?: React.ReactNode;
  /**
   * PLAT-9626: When true, treat the transcript as captions view regardless of
   * the global `videoCreator.getShowSubtitles()` toggle. Used by Content
   * Studio's Captions tab so the caption rendering branch fires without
   * flipping the page-global subtitles toggle (which would also affect
   * Creator Studio). Pairs naturally with `language` for the translated
   * captions render path (PLAT-9621).
   */
  forceCaptionsView?: boolean;
  /**
   * Fired when the user clicks CAPTIONS to enter the captions view AND the
   * track was produced in a non-original (foreign) language. Each surface
   * (Creator Studio, clip preview) wires this to its own `selectedLanguage`
   * setter so the language dropdown auto-selects the captions' actual
   * language — the user shouldn't have to manually match the dropdown to
   * the captions to see them in the right language.
   */
  onCaptionsViewOpen?: (captionsLanguage: string) => void;
  /** When true, suppress the green photo/punch-list highlights on transcript words */
  hidePhotoHighlights?: boolean;
  /**
   * Render-prop invoked when the user hovers a span inside an active quote or
   * clip highlight range. Kept as a render-prop so the shared Transcript
   * component never imports Content-Studio-specific UI.
   */
  renderHighlightPopover?: (info: HighlightHoverInfo) => React.ReactNode;
  /**
   * Invoked from the success toast after Save as Clip resolves, so the
   * caller can deep-link into the clip preview. When undefined, the toast
   * action button is omitted (e.g. inside the clip editor itself).
   */
  onOpenClip?: (clipId: string) => void;
  /**
   * Inline timeline markers (intro card, end card, nametags) for the clip
   * transcript view. Only set by ClipTranscriptBridge; undefined elsewhere
   * leaves the transcript unchanged.
   */
  clipTimelineMarkers?: ClipTimelineMarkers;
  /**
   * PLAT-10045 — invoked when the user clicks a `CardChip` / `NametagChip`
   * in the clip-preview transcript. Receives the marker + the chip's DOM
   * element so the caller can anchor a follow-up popover. Optional.
   */
  onMarkerClick?: MarkerClickHandler;
  /**
   * Commit a new whole-word transcript range for an overlay (drag handles on
   * the underline). When omitted, the adjust-range affordance is hidden.
   * Original-language only — the handles never appear in the translated view.
   *
   * `time`/`duration` (seconds) are resolved here from the boundary words'
   * `ts`/`end_ts` against the transcript's own element array, so the surface
   * commits directly via `commandUpdateElement` without re-deriving against a
   * possibly-mismatched elements array. `startIndex`/`endIndex` are included
   * for logging / analytics.
   *
   * `anchorStartInitialIndex`/`anchorEndInitialIndex` are the boundary words'
   * stable `initial_index`. The surface must rewrite the overlay's
   * `transcriptAnchor` to these — otherwise the anchor-derived underline (see
   * `findOverlayRanges`) re-pins to the old words and the drag visibly snaps
   * back.
   */
  onAdjustOverlayRange?: (
    overlayId: string,
    range: {
      startIndex: number;
      endIndex: number;
      time: number;
      duration: number;
      anchorStartInitialIndex?: number;
      anchorEndInitialIndex?: number;
    },
  ) => void;
  /**
   * When true, suppress the Space/K play-pause shortcut at the transcript
   * scroll-container level. Use in modals that have their own document-level
   * play-pause handler (e.g. ClipPreviewControlBar) to avoid double-firing
   * keydown + keyup which can cancel the toggle out depending on focus.
   */
  suppressPlayShortcut?: boolean;
  /**
   * PLAT-10286 — review panes open anchored to the paused playhead. When true,
   * keep the cursor computed at mount instead of the legacy "reset to {0,0} on
   * mount" guard (which clobbered it); the reset then fires only on an actual
   * video switch. Default false → legacy behavior on every other surface.
   */
  preserveMountCursor?: boolean;
  /**
   * PLAT-10263 — when true, suppress playback-driven auto-scroll in THIS
   * instance (the play-start re-anchor and the per-tick follow). For host
   * surfaces whose transcript stays mounted behind the Translation Review
   * workspace: playback inside the workspace drives the SHARED
   * `videoCreator.isPlaying`/`time`, which would otherwise scroll the hidden
   * background transcript along with review playback. Cursor highlight still
   * updates (cheap, and keeps the position correct on close); only scrolling
   * is suspended. Default false — no behavior change anywhere else.
   */
  suspendAutoScroll?: boolean;
  /**
   * PLAT-10270 (F1) — when true, the Translation Review workspace is open over
   * this transcript. Disables translated-captions break-editing so its window
   * capture-phase Backspace listener never registers while review is open,
   * keeping it disjoint from the review recombine Backspace gesture (SPEC
   * acceptance criterion 5). Default false — no behavior change when absent.
   */
  translationReviewOpen?: boolean;
  /**
   * When set, only paragraphs whose `paragraph_id` appears in this set render
   * in the main text views (regular / redline, original / translated). Karaoke
   * and captions paths ignore this prop. Undefined = no filter (default).
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * When set, paragraphs whose `paragraph_id` appears in this set are rendered
   * with the topic rail in the left gutter (does not filter — all paragraphs
   * still render).
   * Karaoke and captions paths ignore this prop.
   */
  highlightedParagraphIds?: ReadonlySet<string>;
  /**
   * Per-paragraph rail colors keyed by paragraph_id. Looked up when
   * rendering a highlighted or active paragraph; falls back to the default
   * blue when missing. Lets parents assign distinct colors per matched
   * topic when multiple topics are selected.
   */
  paragraphHighlightColors?: ReadonlyMap<string, string>;
  /**
   * Paragraph_id of the currently-active topic match (target of prev/next
   * navigation). Gets a stronger visual treatment than the regular highlight.
   * Karaoke and captions paths ignore this prop.
   */
  activeParagraphId?: string;
  /**
   * Analytics surface for transcript actions (save-as-clip, save-as-pull-quote,
   * replace). One of: 'content_studio_popup' (clip preview transcript tab),
   * 'content_studio_transcript_tab' (story-level transcript tab in Content
   * Studio), or 'creator_studio' (Creator Studio sidepanel transcript).
   */
  surface: TranscriptActionSource;
};

export const Transcript: React.FC<TranscriptProps> = observer(props => {
  const {
    videoCreator: injectedVideoCreator,
    saveService,
    savePullQuoteService,
    clipSaveService,
    savedSegmentService,
    onCombineSegment,
    onHighlightPlaybackRangesChange,
    mediaService,
    hideHeader = false,
    hideCaptions = false,
    hideAddPhoto = false,
    hideAddOverlayGraphic = false,
    onAddOverlayGraphicRequested,
    hideCutText = false,
    hideRemoveText = false,
    hideReplace = false,
    hideSaveAsClip = false,
    showUndoRedo = false,
    showQuotesToggle = false,
    showClipsToggle = false,
    useOriginalTranscript = false,
    language = null,
    enableInlineSpanReplace = false,
    reviewHandoff,
    enableChangedHighlight = false,
    headerExtras = null,
    belowHeader = null,
    forceCaptionsView = false,
    onCaptionsViewOpen,
    hidePhotoHighlights = false,
    renderHighlightPopover,
    onOpenClip,
    clipTimelineMarkers,
    onMarkerClick,
    onAdjustOverlayRange,
    suppressPlayShortcut = false,
    preserveMountCursor = false,
    suspendAutoScroll = false,
    translationReviewOpen = false,
    visibleParagraphIds,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
    surface,
  } = props;
  const videoCreator: ITranscriptVideoCreator = injectedVideoCreator;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdentity = useUserIdentity();
  const transcriptionStore = useTranscription();
  const translationStore = useTranslationStore();
  const karaokeStore = useKaraokeStore();
  const editorStore = useEditor();
  const datoClientStore = useDatoClient();
  const { getSpeakerName: resolvedSpeakerName } = useSpeakerLabels();
  const speakerService = useSpeakerManagementService();
  const { openEditSpeaker, editSpeakerModal } = useEditSpeaker(
    speakerService,
    videoCreator.story?.id,
  );
  const { addTerm: addTermToCompanyDictionary } = useCompanyDictionary();

  // PLAT-11035 — reviewer mode is a property of the user, not of the host, so
  // it is read here rather than threaded in as per-surface hide flags.
  const { enableTranslationReviewerMode } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const [cursor, setCursor] = useState<{ from: number; to: number }>({
    from: 0,
    to: 0,
  });
  // PLAT-9645: ref instead of state. Read inside the cursor-recalc effect
  // as a guard, never rendered. Using useState forced an extra setState on
  // every per-frame time tick, doubling the React commit pressure during
  // playback for no UI benefit. External call sites (delete/cut/click
  // handlers) continue to use the `setLastTimeUpdated` callback below —
  // unchanged signature, just no longer triggers a render.
  const lastTimeUpdatedRef = useRef<number>(0);
  const setLastTimeUpdated = useCallback((time: number) => {
    lastTimeUpdatedRef.current = time;
  }, []);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  // Allows exactly one scroll-to-cursor when entering strikethrough view
  const allowInitialScrollRef = useRef(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuActions, setContextMenuActions] = useState<
    TranscriptContextMenuActions[] | null
  >(null);
  const [contextMenuDisabledReasons, setContextMenuDisabledReasons] =
    useState<TranscriptContextMenuDisabledReasons>({});
  const [showReplaceModal, setShowReplaceModal] = useState<Record<
    'startIndex' | 'endIndex',
    number
  > | null>(null);
  // Redline toggle persisted in the URL (`?redline=1`), plus the instrumentation
  // that catches the param being clobbered by another writer — see
  // `useRedlineUrlState` for both the derivation rules and why it logs.
  const { showHistory, setShowHistory } = useRedlineUrlState({
    searchParams,
    setSearchParams,
    surface,
  });
  // PLAT-9408: keep diff view mounted after first use so re-toggling is
  // instant. The flag is *flipped during render* via a ref (instead of a
  // useEffect) so on the very first redline open the diff mounts in the
  // same commit as `showHistory: true`. With a useEffect the flag would
  // only flip after this render completed, leaving the view-switch
  // useLayoutEffect unable to find any cursor span (regular view's spans
  // are now `display: none` and the diff isn't mounted yet) — so the
  // first-time lock-restore had nothing to anchor against and nothing
  // scrolled. With render-time flipping the diff is in the DOM by the
  // time the layout effect runs.
  const diffEverMountedRef = useRef(showHistory);
  if (showHistory && !diffEverMountedRef.current) {
    diffEverMountedRef.current = true;
  }
  const diffEverMounted = diffEverMountedRef.current;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [activeQuoteIndex, setActiveQuoteIndex] = useState(0);
  const [showClips, setShowClips] = useState(false);
  const [activeClipIndex, setActiveClipIndex] = useState(0);
  const [pendingClipRanges, setPendingClipRanges] = useState<HighlightRange[]>(
    [],
  );
  const pendingActiveQuoteIdRef = useRef<string | null>(null);
  const [showReplaceWarning, setShowReplaceWarning] = useState<boolean>(false);
  const [photoSelection, togglePhotoSelectionModal] = useState<{
    loc: Record<'startIndex' | 'endIndex', number>;
    text: string;
  } | null>(null);
  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);

  const canUndoState = videoCreator.canUndo;
  const canRedoState = videoCreator.canRedo;

  // Speaker context menu state (shown on speaker label click)
  const [speakerMenuInfo, setSpeakerMenuInfo] =
    useState<SpeakerClickInfo | null>(null);

  // Reassign speaker state
  const [reassignSpeakerLabel, setReassignSpeakerLabel] = useState<
    string | null
  >(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignSpeakerData, setReassignSpeakerData] =
    useState<FetchSpeakersResult | null>(null);
  const [showCreateSpeakerModal, setShowCreateSpeakerModal] = useState(false);
  const [reassignFromSelection, setReassignFromSelection] = useState<{
    startIndex: number;
    endIndex: number;
  } | null>(null);
  // Stashed override info: when user clicks "Assign Speaker" on an overridden
  // section, we defer the undo until the modal is confirmed. This holds the
  // section indices so we can undo the override at confirmation time.
  const [pendingOverrideUndo, setPendingOverrideUndo] = useState<{
    sectionIndices: number[];
  } | null>(null);

  // Speaker section override operations (undo/redo, reassign, background save)
  const speakerOverrides = useSpeakerOverrides();

  // Create adapter and hook for business logic
  // Use videoCreator directly - no adapter needed
  const showSubtitles = videoCreator.getShowSubtitles();

  const scrollRef = React.useRef<HTMLDivElement>(null);
  // Ref to the actual scrollable element (`ScrollableText`, the div with
  // `id="transcript-scrollable-container"`). Required because
  // `document.getElementById` returns the first match in document order, so
  // when Content Studio's clip-preview Transcript is mounted on top of
  // Creator Studio's, both have an element with that id and the unscoped
  // lookup picks Creator Studio's. That broke `captureCursorYLock` and
  // `scrollToPlayhead` in clip preview — they measured against and scrolled
  // the wrong container, so the lock-restore never ran on the visible view.
  const scrollableContainerRef = React.useRef<HTMLDivElement>(null);
  const originalTranscription = transcriptionStore.getOriginalTranscription();
  const hasKaraokeProduced = karaokeStore.hasKaraokeLines;
  // PLAT-9626: Content Studio's Captions tab forces this on without flipping
  // `videoCreator.showSubtitles` (page-global). Falls back to the legacy
  // showSubtitles-driven path everywhere else.
  const isInKaraokeView =
    (showSubtitles || forceCaptionsView) && hasKaraokeProduced;

  // Auto-sync the language dropdown to the captions track's actual
  // language. Fires whenever captions view is active and the track is
  // produced in a non-original language that differs from the
  // dropdown's current selection. Covers all entry paths (initial
  // mount with captions already on, clicking CAPTIONS to toggle on,
  // captions getting produced while the view is open). Each consumer
  // wires `onCaptionsViewOpen` to its own `selectedLanguage` setter
  // so this effect is the single source of truth for that sync —
  // separate from the user-driven dropdown change, which goes the
  // other direction (dropdown → re-produce karaoke in chosen lang).
  const captionsConfigLang = karaokeStore.defaultKaraokeConfig?.language;
  React.useEffect(() => {
    if (!isInKaraokeView) return;
    if (!captionsConfigLang || captionsConfigLang === 'original') return;
    if (language === captionsConfigLang) return;
    if (!onCaptionsViewOpen) return;
    onCaptionsViewOpen(captionsConfigLang);
  }, [
    isInKaraokeView,
    captionsConfigLang,
    language,
    onCaptionsViewOpen,
    showSubtitles,
    forceCaptionsView,
    hasKaraokeProduced,
    karaokeStore.defaultKaraokeConfig,
  ]);

  const karaokeLines = karaokeStore.getKaraokeLinesFormatted();

  const displayElements = useDisplayTranscriptElements(isInKaraokeView);
  const transcriptElements = useOriginalTranscript
    ? (originalTranscription?.elements as typeof displayElements)
    : displayElements;

  // Lazy-compute blocks on first redline toggle, then cache for instant
  // subsequent toggles. The cache is a write-through ref populated inside
  // `useMemo` — refs don't trigger re-renders, so this is safe to do during
  // render and avoids the render-vs-effect ordering hazard of a follow-up
  // `useEffect` (which would leave the cache empty on the same-tick
  // off-toggle).
  const transcriptBlocksCacheRef = useRef<{
    key: typeof transcriptElements;
    blocks: ReturnType<typeof getTranscriptElementBlocks>;
    split: ReturnType<typeof getTranscriptElementBlocks>;
  } | null>(null);

  const transcriptBlocks = useMemo(() => {
    const cached = transcriptBlocksCacheRef.current;
    if (cached && cached.key === transcriptElements) {
      return cached.blocks;
    }
    if (!showHistory || !transcriptElements) {
      return [];
    }
    const blocks = computed(() =>
      getTranscriptElementBlocks(
        transcriptElements,
        transcriptionStore.getOriginalTranscription()?.elements || [],
      ),
    ).get();
    return blocks;
  }, [showHistory, transcriptElements]);

  // Split blocks at paragraph boundaries for correct selection lookups in useTranscriptOperations
  const splitTranscriptBlocks = useMemo(() => {
    const cached = transcriptBlocksCacheRef.current;
    if (cached && cached.key === transcriptElements) {
      return cached.split;
    }
    if (transcriptBlocks.length === 0) return [];
    const groups = groupBlocksByParagraph(transcriptBlocks);
    const split = groups.flatMap(group => group.blocks);
    // Write-through cache: only populate once both arrays exist for this
    // `transcriptElements` reference.
    transcriptBlocksCacheRef.current = {
      key: transcriptElements,
      blocks: transcriptBlocks,
      split,
    };
    return split;
  }, [transcriptBlocks, transcriptElements]);

  // Phrase-step navigation for the foreign-language transcript views.
  // When a translation language is selected, arrow keys should advance
  // by phrase (one visible "section") rather than by source word —
  // otherwise multiple in-phrase arrow presses produce no visible cursor
  // motion because the visual cursor is driven by `currentTime` →
  // `currentInitialIndex` (which only advances on phrase boundaries).
  // Stops are one per phrase, except that a full-width phrase sub-steps
  // through its own source words (PLAT-11483) — the splitter cannot
  // fragment a reordering language, so a Japanese phrase is often a whole
  // sentence. See `translatedWordAtoms.ts` for the model + diagnosis.
  const phraseStepIndices = useMemo<number[] | undefined>(() => {
    if (!language || !transcriptElements) return undefined;
    const translation = translationStore.getTranslation(language);
    if (!translation || translation.phrases.length === 0) return undefined;
    return buildPhraseStepIndices(translation.phrases, transcriptElements);
  }, [language, transcriptElements, translationStore]);

  // PLAT-9626: lines for the captions view. Captions are a produced-only
  // artifact — the hook returns the produced translated lines (with lead
  // identity attached) or an empty array, in which case the caller falls back
  // to the original-language view. A translation edit reaches captions via a
  // scoped karaoke re-produce. See `useTranslatedCaptionLines` for the contract.
  const translatedCaptionLines = useTranslatedCaptionLines(
    karaokeStore.karaokeLines,
    language,
  );

  // Services are now injected via props
  // (no longer instantiated inline)

  // PLAT-10646 — carries the source element range mapped from a translated
  // phrase while one context-menu action dispatches. The translated DOM has
  // no data-index spans, so the hook's DOM-selection resolvers consult this
  // override first. A ref (not state): handlers read it synchronously
  // mid-dispatch.
  const translatedRangeOverrideRef = useRef<{
    startIndex: number;
    endIndex: number;
  } | null>(null);

  // Initialize business logic hook
  const {
    selectedWordIndex,
    setSelectedWordIndex,
    getBoundaryIndexesFromSelection,
    handleCursorMove,
    handleAddPunctuation,
    handleAddKaraokeBreak,
    handleToggleCaptionSoftBreak,
    handleTranscriptRestore,
    getSelectedStartEndIndex,
    handleHideKaraoke,
    handleTranscriptDelete,
    getCursorPosition,
    skipToWordWithIndex,
    getActions,
    handleRemovePhoto,
    handleAddPhoto,
    resolveTranscriptSelection,
    handleCutText,
    handlePasteText,
    handleSaveAsClip,
    handleTranscriptTextReplace,
    convertIndexToSegmentBoundaries,
  } = useTranscriptOperations(
    videoCreator,
    editorStore,
    datoClientStore,
    clipSaveService,
    mediaService,
    transcriptElements,
    showHistory,
    splitTranscriptBlocks,
    karaokeLines,
    karaokeStore.hasKaraokeLines,
    translatedRangeOverrideRef,
  );

  const { commandProduceKaraoke } = useKaraoke(editorStore.editor);

  // PLAT-9623: synthetic-break command bindings for the translated
  // captions view. Null when the editor isn't ready yet so the view
  // falls back to read-only.
  const syntheticBreakActions = React.useMemo(() => {
    const editor = editorStore.editor;
    if (!editor) return null;
    return {
      add: (
        lineId: string,
        phraseOriginalIndices: number[],
        textPosition: number,
      ) => {
        editor.commandAddSyntheticCaptionBreak(
          lineId,
          phraseOriginalIndices,
          textPosition,
        );
      },
      remove: (breakId: string) => {
        editor.commandRemoveSyntheticCaptionBreak(breakId);
      },
      // PLAT-10945: the join can be refused — two rows belonging to different
      // speakers must not become one row. `mergeCaptionRows` performs the join
      // and surfaces that refusal as a toast; the boolean is not needed here.
      merge: (rightLineId: string) => {
        mergeCaptionRows(editor, rightLineId, toast => {
          videoCreator.toastState = toast;
        });
      },
    };
  }, [editorStore.editor, videoCreator]);

  const [debugEnabled, setDebugEnabled] = useState(false);

  // PLAT-9646: keep latest cursor in a ref so the isPlaying-transition effect
  // below can read the freshest value without re-firing on every cursor tick.
  const cursorRef = useRef(cursor);
  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  // PLAT-9646: lock the cursor's Y offset across the next redline toggle.
  // Lets the eye stay fixed on the same line when toggling mid-playback,
  // instead of every switch re-anchoring at PLAYHEAD_TOP_OFFSET_RATIO.
  //
  // Lifecycle (single-owner, single-consumer per toggle):
  //   1. SET by `captureCursorYLock()` synchronously before a state flip
  //      that triggers a view switch — invoked from:
  //      - the redline toggle button's `onClick` (this file), and
  //      - the `REQUEST_CURSOR_Y_LOCK_EVENT` listener (this file), which
  //        sibling components dispatch before language changes.
  //   2. READ by `scrollToPlayhead`. On `tryLockOnly=true` (the sync pass
  //      of the view-switch `useLayoutEffect`), if the cursor element in
  //      the new view isn't measurable yet, the ref is preserved and the
  //      function bails — letting the rAF retry consume it next frame.
  //      On the retry (or any non-`tryLockOnly` call), the ref is read,
  //      the scroll is applied, and the ref is cleared to `null`.
  //   3. CLEARED by `scrollToPlayhead` after a successful application
  //      (lock-restore branch returns) OR after the fall-through to the
  //      ratio anchor (lock didn't apply but we're committing to the
  //      non-lock path).
  //
  // Invariant: the ref must be `null` between toggles. If you add a new
  // path that calls `captureCursorYLock`, ensure a `scrollToPlayhead`
  // call follows it within the same React commit cycle (or schedule one)
  // so the lock can't leak into an unrelated `scrollToPlayhead` (e.g.
  // the playback-start effect) and produce a confused scroll.
  const lockedCursorYOffsetRef = useRef<number | null>(null);

  // Mirror `transcriptElements` into a ref so `scrollToPlayhead` can resolve
  // a cursor element's `initial_index` without taking the array as a
  // useCallback dep (which would re-create the callback — and re-fire the
  // isPlaying effect that consumes it — on every transcript edit).
  const transcriptElementsRef = useRef(transcriptElements);
  useEffect(() => {
    transcriptElementsRef.current = transcriptElements;
  }, [transcriptElements]);

  // PLAT-9646: capture the cursor's current Y offset within the scroll
  // container, used by the redline toggle handler and the
  // REQUEST_CURSOR_Y_LOCK_EVENT listener (fired by language toggles in
  // sibling components). One source of truth so all "view-switch" entry
  // points behave the same way.
  const captureCursorYLock = useCallback(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;
    const cursorTo = cursorRef.current.to;
    const cursorElData = transcriptElementsRef.current?.[cursorTo];
    const initialIndex = cursorElData?.initial_index;
    // PLAT-10611 (F5, R6) — the cursor element's stable id for the id-first lookup.
    const sourceUuid = cursorElData?.source_uuid ?? cursorElData?.id;
    const cursorEl = findVisibleCursorElement(
      container,
      cursorTo,
      initialIndex,
      sourceUuid,
    );
    if (!cursorEl) return;
    const containerRect = container.getBoundingClientRect();
    const cursorRect = cursorEl.getBoundingClientRect();
    // Capture Y *unconditionally* — including when the cursor is currently
    // off-screen. The lock-restore math then preserves that same off-screen
    // offset in the new view, so toggling regular ⇌ redline causes no
    // visible motion regardless of where the cursor was. (Previously this
    // path skipped when the cursor wasn't fully within the container, so
    // no lock was set in clip preview where the modal opens at scrollTop=0
    // with the cursor mid-doc — and the open-anchor fallback then scrolled
    // visibly. Mirrors `TranslatedTranscriptView`'s unconditional capture.)
    const yOffset = cursorRect.top - containerRect.top;
    // Clamp to the project-wide cursor-top-offset floor so the lock can't
    // pin the cursor right at the top in the new view (which read as
    // "jumped back up" on second open). The clamp only kicks in when the
    // cursor was sitting near the top of the visible range; off-screen-
    // below cursors keep their full Y.
    const minYOffset = computeLineHeightPx(cursorEl) * CURSOR_TOP_OFFSET_LINES;
    lockedCursorYOffsetRef.current = Math.max(yOffset, minYOffset);
  }, []);

  // Listen for external lock requests (e.g. language toggle in
  // LeftSidePanel / ClipTranscriptBridge). Caller dispatches the event
  // synchronously BEFORE its state change, so the handler measures the
  // OLD DOM before re-render.
  useEffect(() => {
    const handler = () => captureCursorYLock();
    window.addEventListener(REQUEST_CURSOR_Y_LOCK_EVENT, handler);
    return () => {
      window.removeEventListener(REQUEST_CURSOR_Y_LOCK_EVENT, handler);
    };
  }, [captureCursorYLock]);

  // PLAT-9646: scroll the transcript container so the playhead lands at
  // PLAYHEAD_TOP_OFFSET_RATIO from the top — but ONLY if it isn't already
  // visible. If the user is already looking at the right spot (e.g. they
  // toggled redline mid-clip and the playhead is in the viewport), don't
  // yank them. After scrolling, if playback is paused the view stays put;
  // if playing, the per-tick auto-scroll effect (below) takes over.
  const scrollToPlayhead = useCallback(
    (cursorTo: number, tryLockOnly = false) => {
      const container = scrollableContainerRef.current;
      if (!container) return;

      // If a Y offset was locked by the redline toggle, anchor the cursor
      // at that exact pixel position so the eye stays on the same line.
      // `findVisibleCursorElement` covers both attribute schemes — the
      // original-language views (`data-index`) and the translated views
      // (`data-initial-index`) — so the lock works across all four
      // toggle directions.
      const lockedOffset = lockedCursorYOffsetRef.current;
      const lockCursorElData = transcriptElementsRef.current?.[cursorTo];
      const initialIndex = lockCursorElData?.initial_index;
      // PLAT-10611 (F5, R6) — id-first cursor lookup on view switch.
      const sourceUuid = lockCursorElData?.source_uuid ?? lockCursorElData?.id;
      const cursorElLocked =
        lockedOffset !== null
          ? findVisibleCursorElement(
              container,
              cursorTo,
              initialIndex,
              sourceUuid,
            )
          : null;
      const decision = computeLockRestoreDecision(
        container.scrollTop,
        container.getBoundingClientRect(),
        cursorElLocked ? cursorElLocked.getBoundingClientRect() : null,
        lockedOffset,
        tryLockOnly,
      );
      if (decision.kind === 'apply') {
        bypassScrollBlocker(container, { top: decision.scrollTop });
        lockedCursorYOffsetRef.current = null;
        allowInitialScrollRef.current = false;
        return;
      }
      if (decision.kind === 'preserve-lock') {
        // Sync `tryLockOnly` pass: the just-mounted view's layout isn't
        // settled yet. Leave the lock in place; the caller (view-switch
        // useLayoutEffect) will retry on the next animation frame.
        return;
      }
      // Fall-through: either no lock was set, or the lock couldn't be
      // applied AND we aren't in `tryLockOnly` mode. Clear the lock and
      // proceed to the non-lock anchor (lookahead at
      // PLAYHEAD_TOP_OFFSET_RATIO).
      lockedCursorYOffsetRef.current = null;

      const cursorEl =
        findVisibleByDataIndex(container, cursorTo) ??
        findByForwardInitialIndexScan(
          container,
          cursorTo,
          transcriptElementsRef.current,
        ) ??
        findVisibleDiffBlockForIndex(container, cursorTo);
      if (cursorEl) {
        const containerRect = container.getBoundingClientRect();
        const cursorRect = cursorEl.getBoundingClientRect();
        if (
          cursorRect.top >= containerRect.top &&
          cursorRect.bottom <= containerRect.bottom
        ) {
          allowInitialScrollRef.current = false;
          return;
        }
      }
      const lookahead =
        cursorTo < CURSOR_LOOKAHEAD_EARLY_THRESHOLD
          ? CURSOR_LOOKAHEAD_EARLY
          : CURSOR_LOOKAHEAD_LATE;
      const targetIndex = Math.min(
        cursorTo + lookahead,
        (transcriptElements?.length || 0) - 1,
      );
      const el =
        findVisibleByDataIndex(container, targetIndex) ??
        findByForwardInitialIndexScan(
          container,
          targetIndex,
          transcriptElementsRef.current,
        ) ??
        findVisibleDiffBlockForIndex(container, targetIndex);
      if (el) {
        const scrollTop = computePlayheadScrollTop(container, el);
        // Bypass the redline scroll blocker — initial scroll on view switch.
        bypassScrollBlocker(container, { top: scrollTop });
      }
      allowInitialScrollRef.current = false;
    },
    [transcriptElements?.length],
  );

  // autoscroll transcript when playing
  useEffect(() => {
    // PLAT-10263: a host can suspend playback-driven scrolling while a modal
    // (the Translation Review workspace) drives the shared playback state —
    // this hidden instance must not re-anchor or follow. Skip the state sync
    // too: on un-suspend the next isPlaying flip re-runs this effect.
    if (suspendAutoScroll) return;
    setAutoScroll(videoCreator.isPlaying);
    // PLAT-9646: when playback starts, scroll-to-playhead immediately if it's
    // not in view. The per-tick auto-scroll effect waits for the next cursor
    // change (~100ms after time advances) AND only scrolls when the lookahead
    // target leaves the viewport — so the playhead can sit unseen for several
    // seconds after pressing play. This makes "press play" always re-anchor
    // the view on the current word in any view (including redline).
    if (videoCreator.isPlaying) {
      scrollToPlayhead(cursorRef.current.to);
    }
  }, [videoCreator.isPlaying, scrollToPlayhead, suspendAutoScroll]);

  // recalculate cursor position when switching views (indexes change).
  // useLayoutEffect — runs synchronously after commit, before paint. The
  // toggle flips the diff's parent display from `none` to `contents`, which
  // re-lays out a thousands-of-spans subtree; if we scrolled inside a regular
  // useEffect (or a rAF chain), the user would see one paint at scrollTop=0
  // followed by a paint at the lock-restore position — a visible "two
  // moves" jump. Doing the scroll inside useLayoutEffect collapses both
  // into a single paint.
  //
  // First-mount gate: this effect runs on mount as well as on real
  // view switches. On mount we still want `setCursor(newCursor)` so the
  // initial cursor is correct, but we MUST NOT call `scrollToPlayhead` —
  // doing so in clip preview (modal opens at scrollTop=0 with the cursor
  // mid-doc, no lock set) falls through to the lookahead anchor and
  // visibly scrolls the just-opened modal. Mirror the
  // `previousHideRef`/`modeChanged` pattern in
  // `TranslatedTranscriptView.tsx`.
  const prevViewKeyRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const newCursor = getCursorPosition();
    setCursor(newCursor);

    const viewKey = `${isInKaraokeView}|${showHistory}|${language ?? ''}`;
    const isFirstRun = prevViewKeyRef.current === null;
    prevViewKeyRef.current = viewKey;
    if (isFirstRun) {
      // Mount: leave scrollTop alone. Clip preview opens at scrollTop=0
      // with cursor mid-doc; the previous (pre-PLAT-9646) behavior didn't
      // scroll on initial regular-view mount, and we restore that here.
      return;
    }

    // PLAT-9645 follow-up: re-engage auto-scroll on every view switch (both
    // directions). Previously this only fired when LEAVING redline, leaving
    // the user stuck at the top of the redline view if they had scrolled
    // earlier (onWheel/onTouchMove disable autoScroll). Toggling redline is
    // an explicit re-engagement signal — the user wants to focus on the
    // current playhead in the new view.
    setAutoScroll(true);
    allowInitialScrollRef.current = true;
    // Two-phase attempt:
    //   1) Sync, lock-only. Succeeds when the new view's cursor span is
    //      already laid out — the common case for *subsequent* toggles
    //      where the diff was already mounted and just had its display
    //      flipped. Single paint, no visible motion.
    //   2) If sync couldn't measure the cursor (the very first toggle:
    //      the diff component just mounted in this commit, and its
    //      children haven't finished layout by useLayoutEffect time),
    //      preserve the lock and retry on the next animation frame.
    //      Without (2), sync would have fallen through to the lookahead
    //      anchor and dropped the cursor at the top of the viewport,
    //      so the first toggle landed in a different place from every
    //      subsequent toggle.
    scrollToPlayhead(newCursor.to, /* tryLockOnly */ true);
    if (lockedCursorYOffsetRef.current !== null) {
      const raf = requestAnimationFrame(() => {
        scrollToPlayhead(newCursor.to);
      });
      return () => cancelAnimationFrame(raf);
    }
    // PLAT-9646: include `language` so this fires on language toggles too —
    // without it, switching between es-MX → fr-FR → original (or any pair)
    // would leave the cursor un-anchored in the new view and the user would
    // lose their reading position.
  }, [isInKaraokeView, showHistory, language]);

  useEffect(() => {
    if (suspendAutoScroll || !autoScroll || !videoCreator.isPlaying) return;
    const lookahead =
      cursor.to < CURSOR_LOOKAHEAD_EARLY_THRESHOLD
        ? CURSOR_LOOKAHEAD_EARLY
        : CURSOR_LOOKAHEAD_LATE;
    const targetIndex = Math.min(
      cursor.to + lookahead,
      (transcriptElements?.length || 0) - 1,
    );
    requestAnimationFrame(() => {
      const container = scrollableContainerRef.current;
      if (!container) return;
      // Translated views tag spans with `data-initial-index` instead of
      // `data-index`, so the data-index lookup returns null in them.
      // Fallback walks forward from `targetIndex` looking for a rendered
      // phrase lead. Redline view tags only block starts with `data-index`,
      // so the diff-range scan catches mid-block targets there.
      const el =
        findVisibleByDataIndex(container, targetIndex) ??
        findByForwardInitialIndexScan(
          container,
          targetIndex,
          transcriptElementsRef.current,
        ) ??
        findVisibleDiffBlockForIndex(container, targetIndex);
      if (!el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const isTargetVisible =
        elRect.top >= containerRect.top &&
        elRect.bottom <= containerRect.bottom;
      if (isTargetVisible) return;
      const scrollTop = computePlayheadScrollTop(container, el);
      container.scrollTo({ top: scrollTop });
    });
  }, [cursor, autoScroll, showHistory, suspendAutoScroll]);

  // Block programmatic scrolling in strikethrough view unless playing
  useEffect(() => {
    if (!showHistory) return;
    const container = scrollableContainerRef.current;
    if (!container) return;
    const originalScrollTo = container.scrollTo.bind(container);
    container.scrollTo = function (
      ...args: Parameters<typeof container.scrollTo>
    ) {
      // Allow scrolling when video is playing, block otherwise.
      if (videoCreator.isPlaying) {
        originalScrollTo(...args);
      }
    } as typeof container.scrollTo;
    return () => {
      container.scrollTo = originalScrollTo;
    };
  }, [showHistory]);

  // highlight words as video playing
  // PLAT-9645: deps are [transcriptElements, videoCreator.time] only —
  // dropped `getCursorPosition` because its identity changes on every
  // `videoCreator.time` tick (it's a useCallback whose deps include time),
  // making the effect re-fire twice per tick. Also coalesced two setStates
  // into one (lastTimeUpdated is now a ref, never a render trigger).
  useEffect(() => {
    if (
      !transcriptElements ||
      (videoCreator.isPlaying &&
        videoCreator.time < lastTimeUpdatedRef.current &&
        lastTimeUpdatedRef.current < videoCreator.time + 5) ||
      Math.abs(lastTimeUpdatedRef.current - videoCreator.time) <
        CURSOR_RECALC_MIN_DELTA_SEC
    ) {
      return;
    }
    const nextCursor = getCursorPosition();
    lastTimeUpdatedRef.current = videoCreator.time;
    setCursor(nextCursor);
  }, [transcriptElements, videoCreator.time, getCursorPosition]);

  // reset cursor on story or video change
  const prevVideoIdRef = useRef(videoCreator.currentVideo?.id);
  useEffect(() => {
    const prevId = prevVideoIdRef.current;
    const currentId = videoCreator.currentVideo?.id;
    // Review panes (preserveMountCursor) open anchored to the paused playhead,
    // so they reset ONLY on an actual switch to a different video. The legacy
    // guard ("both ids truthy") also fired on mount, clobbering the open cursor
    // to {0,0}; while paused the MIN_DELTA recalc gate then blocked recovery,
    // so the playhead caret never showed at open until playback moved the clock
    // (PLAT-10286). `prevId !== currentId` also skips a save/load's
    // `id → undefined → id` transitions (one side is always falsy). Every other
    // surface keeps the legacy mount-reset behavior, unchanged.
    const shouldReset = preserveMountCursor
      ? prevId && currentId && prevId !== currentId
      : prevId && currentId;
    if (shouldReset) {
      setCursor({ from: 0, to: 0 });
    }
    prevVideoIdRef.current = currentId;
  }, [
    videoCreator.story?.id,
    videoCreator.currentVideo?.id,
    preserveMountCursor,
  ]);

  useEffect(() => {
    if (showHistory && showSubtitles) {
      videoCreator.setShowSubtitles(!showSubtitles);
    }
  }, [showHistory]);

  useEffect(() => {
    if (showSubtitles && showHistory) {
      setShowHistory(!showHistory);
    }
  }, [showSubtitles]);

  // --- Shared highlight helpers ---
  const getScrollContainer = useCallback(() => {
    return scrollableContainerRef.current ?? scrollRef.current ?? undefined;
  }, []);

  // Returns the index of the first visible range, or 0 if none visible
  const getInitialVisibleIndex = useCallback(
    (ranges: { id: string; start: number; end: number }[]) => {
      const container = scrollRef.current;
      const scrollable = getScrollContainer();
      if (container && scrollable && ranges.length > 0) {
        const idx = findFirstVisibleRangeIndex(container, ranges, scrollable);
        return idx >= 0 ? idx : 0;
      }
      return 0;
    },
    [getScrollContainer],
  );

  // --- Quotes highlight logic ---
  // Ranges are always computed so findFirstVisibleRangeIndex works on toggle
  const shareableImages = videoCreator.story?.shareableImages;
  const quoteRanges = useMemo(() => {
    if (!transcriptElements?.length) return [];
    const quotes = shareableImages || [];
    if (!quotes.length) return [];
    return findQuoteRanges(transcriptElements, quotes);
  }, [transcriptElements, shareableImages]);

  useEffect(() => {
    const pendingId = pendingActiveQuoteIdRef.current;
    if (!pendingId) return;
    const idx = quoteRanges.findIndex(r => r.id === pendingId);
    if (idx >= 0 && idx !== activeQuoteIndex) {
      setActiveQuoteIndex(idx);
    }
  }, [quoteRanges, activeQuoteIndex]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    clearQuoteHighlights(container);
    if (showQuotes && quoteRanges.length > 0) {
      const allIndices = rangesToIndices(quoteRanges);
      const pendingId = pendingActiveQuoteIdRef.current;
      let activeRange: HighlightRange | null =
        quoteRanges[activeQuoteIndex] || null;
      if (pendingId && activeRange?.id !== pendingId) {
        activeRange = null;
      } else if (pendingId && activeRange?.id === pendingId) {
        pendingActiveQuoteIdRef.current = null;
      }
      applyQuoteHighlights(
        container,
        allIndices,
        activeRange,
        getScrollContainer(),
      );
    }
    return () => {
      clearQuoteHighlights(container);
    };
  }, [showQuotes, quoteRanges, activeQuoteIndex, getScrollContainer]);

  const goNextQuote = useCallback(() => {
    if (quoteRanges.length === 0) return;
    setActiveQuoteIndex((activeQuoteIndex + 1) % quoteRanges.length);
  }, [quoteRanges.length, activeQuoteIndex, setActiveQuoteIndex]);

  const goPrevQuote = useCallback(() => {
    if (quoteRanges.length === 0) return;
    setActiveQuoteIndex(
      (activeQuoteIndex - 1 + quoteRanges.length) % quoteRanges.length,
    );
  }, [quoteRanges.length, activeQuoteIndex, setActiveQuoteIndex]);

  // Shared by the pill's label and its dimmed nav arrows (PLAT-11039).
  const handleToggleQuotes = useCallback(() => {
    analytics.track('transcript_toggle_quotes', {
      storyId: videoCreator.story?.id,
      storyTitle: videoCreator.story?.title,
    });
    if (!showQuotes) {
      setActiveQuoteIndex(getInitialVisibleIndex(quoteRanges));
    }
    setShowQuotes(!showQuotes);
  }, [
    showQuotes,
    quoteRanges,
    setActiveQuoteIndex,
    getInitialVisibleIndex,
    videoCreator.story,
  ]);

  // --- Clips highlight logic ---
  // Ranges are always computed so findFirstVisibleRangeIndex works on toggle
  const otherVideos = videoCreator.story?.otherVideos;
  const realClipRanges = useMemo(
    () => findClipRanges(otherVideos || []),
    [otherVideos],
  );
  const clipRanges = useMemo(() => {
    if (pendingClipRanges.length === 0) return realClipRanges;
    const realKeys = new Set(realClipRanges.map(r => `${r.start}-${r.end}`));
    const pending = pendingClipRanges.filter(
      r => !realKeys.has(`${r.start}-${r.end}`),
    );
    return [...realClipRanges, ...pending].sort((a, b) => a.start - b.start);
  }, [realClipRanges, pendingClipRanges]);

  // --- Saved-segment highlight logic (save-for-later → combine) ---
  const savedSegmentsEnabled = !!savedSegmentService;
  const savedSegmentsMap = videoCreator.story?.savedSegments;
  const savedSegmentRanges = useMemo(
    () =>
      savedSegmentsEnabled
        ? findSavedSegmentRanges(
            savedSegmentsMap,
            transcriptElements?.length ?? 0,
          )
        : [],
    [savedSegmentsEnabled, savedSegmentsMap, transcriptElements?.length],
  );

  // Existing groups offered as "Save to {name}" targets in the context menu
  // and listed in the header's Saved Segments filter. Each carries its
  // persisted color (falling back to the default violet for older segments).
  const savedSegmentTargets = useMemo(
    () =>
      savedSegmentsEnabled && savedSegmentsMap
        ? Object.entries(savedSegmentsMap).map(([key, group]) => {
            // Previews = first sentence of each range the group covers, in
            // transcript order, shown on row hover. The row itself is named for
            // its color, so this is the only place the highlight's actual text
            // appears — and a group that was appended to holds several passages,
            // so all of them are listed, not just the opening one.
            const previews = [...group.ranges]
              .sort((a, b) => a.startIndex - b.startIndex)
              .map(range =>
                firstSentencePreview(
                  extractTranscriptSelectionText(
                    transcriptElements,
                    range.startIndex,
                    range.endIndex,
                  ),
                ),
              )
              .filter(Boolean);
            return {
              key,
              name: group.name,
              color: group.color ?? DEFAULT_SAVED_SEGMENT_COLOR,
              rangeCount: group.ranges.length,
              previews,
            };
          })
        : [],
    [savedSegmentsEnabled, savedSegmentsMap, transcriptElements],
  );

  // Range id (group key) → color, for per-segment underline coloring.
  const savedSegmentColorByKey = useMemo(() => {
    const map: Record<string, string> = {};
    if (savedSegmentsMap) {
      for (const [key, group] of Object.entries(savedSegmentsMap)) {
        if (group.color) map[key] = group.color;
      }
    }
    return map;
  }, [savedSegmentsMap]);

  // --- Saved-segment filter (header dropdown): Highlight | Show Only ---
  // Empty `selectedSavedSegmentKeys` means "Show All": every segment is
  // underlined (highlight) / nothing is hidden (show-only). `savedSegmentsHidden`
  // is the explicit off state — highlights start visible and toggling "Show All"
  // off hides them all regardless of selection.
  const [savedSegmentFilterMode, setSavedSegmentFilterMode] =
    useState<TopicFilterMode>('highlight');
  const [selectedSavedSegmentKeys, setSelectedSavedSegmentKeys] = useState<
    string[]
  >([]);
  const [savedSegmentsHidden, setSavedSegmentsHidden] = useState(false);

  // Drop selected keys that no longer exist (segment removed / story swapped),
  // preserving the array reference when nothing is stale so this is a no-op
  // for benign additions.
  useEffect(() => {
    const validKeys = new Set(savedSegmentTargets.map(t => t.key));
    setSelectedSavedSegmentKeys(prev =>
      prev.length > 0 && !prev.every(k => validKeys.has(k))
        ? prev.filter(k => validKeys.has(k))
        : prev,
    );
  }, [savedSegmentTargets]);

  // The ranges the current selection points at, ignoring the hidden toggle.
  const selectedSavedSegmentRanges = useMemo(() => {
    if (selectedSavedSegmentKeys.length === 0) return savedSegmentRanges;
    const keys = new Set(selectedSavedSegmentKeys);
    return savedSegmentRanges.filter(r => keys.has(r.id));
  }, [savedSegmentRanges, selectedSavedSegmentKeys]);

  // Underlined ranges: none when hidden, the selected segments, or all of them
  // when "Show All".
  const filteredSavedSegmentRanges = useMemo(
    () => (savedSegmentsHidden ? [] : selectedSavedSegmentRanges),
    [savedSegmentsHidden, selectedSavedSegmentRanges],
  );

  // Guards key on the MODES, not the lapsing sets — see useSavedSegmentVisibility.
  const savedSegmentShowOnly = savedSegmentFilterMode === 'show-only';
  // Normalize headerExtras: a plain node is tail content (PLAT-11039).
  const extraSlots: HeaderExtrasSlots = isHeaderExtrasSlots(headerExtras)
    ? headerExtras
    : { trailing: headerExtras };
  const {
    visibleElementIndices: savedSegmentVisibleElementIndices,
    effectiveVisibleParagraphIds,
    showOnlyActive,
  } = useSavedSegmentVisibility({
    showOnly: savedSegmentShowOnly,
    hostShowOnly: !!extraSlots.filtersOwnShowOnly,
    ranges: filteredSavedSegmentRanges,
    transcriptElements,
    visibleParagraphIds,
  });

  // PLAT-10949 — in show-only mode the video should ALSO play only the
  // highlights: convert the active element-index ranges to time windows and
  // report them to the host (which threads them into its preview player).
  // Null outside show-only mode / with no active ranges = normal playback.
  const highlightPlaybackRanges = useMemo(() => {
    if (
      !onHighlightPlaybackRangesChange ||
      savedSegmentFilterMode !== 'show-only' ||
      filteredSavedSegmentRanges.length === 0
    ) {
      return null;
    }
    const ranges = highlightRangesToPlaybackRanges(
      filteredSavedSegmentRanges,
      transcriptElements,
    );
    return ranges.length > 0 ? ranges : null;
  }, [
    onHighlightPlaybackRangesChange,
    savedSegmentFilterMode,
    filteredSavedSegmentRanges,
    transcriptElements,
  ]);

  // `transcriptElements` is a fresh array on every render (see CLAUDE.md
  // pitfall #18), so the memo above recomputes each render and hands back a new
  // array identity even when nothing changed. Push to the host only when the
  // VALUE changes — otherwise the host's setState re-renders us, we produce
  // another new array, and the cycle never settles.
  const lastPushedRangesRef = useRef<string | null>(null);
  useEffect(() => {
    const key = highlightPlaybackRanges
      ? JSON.stringify(highlightPlaybackRanges)
      : null;
    if (key === lastPushedRangesRef.current) return;
    lastPushedRangesRef.current = key;
    onHighlightPlaybackRangesChange?.(highlightPlaybackRanges);
  }, [highlightPlaybackRanges, onHighlightPlaybackRangesChange]);

  // Clear on unmount so a stale filter never keeps constraining the player.
  useEffect(
    () => () => onHighlightPlaybackRangesChange?.(null),
    [onHighlightPlaybackRangesChange],
  );

  // One value for the left edge: the header and the scroll container both take
  // it, so the toolbar and the transcript text always line up (PLAT-11039).
  const topicGutter = reservesTopicGutter(
    highlightedParagraphIds,
    activeParagraphId,
  );
  // Entering any Show Only clears every other mark (PLAT-11182): quotes/clips,
  // the redline view (inert while blocked, so it must not be stuck on), and —
  // when the HOST's filter took the mode — the highlight selection and wash.
  const hostShowOnly = !!extraSlots.filtersOwnShowOnly;
  useEffect(() => {
    if (!showOnlyActive) return;
    setShowQuotes(false);
    setShowClips(false);
    setShowHistory(false);
    if (hostShowOnly) {
      setSelectedSavedSegmentKeys([]);
      setSavedSegmentsHidden(true);
    }
  }, [showOnlyActive, hostShowOnly, setShowHistory]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    clearClipHighlights(container);
    if (showClipsToggle && showClips && clipRanges.length > 0) {
      const allIndices = rangesToIndices(clipRanges);
      applyClipHighlights(
        container,
        allIndices,
        clipRanges[activeClipIndex] || null,
        getScrollContainer(),
      );
    }
    return () => {
      clearClipHighlights(container);
    };
  }, [
    showClipsToggle,
    showClips,
    clipRanges,
    activeClipIndex,
    getScrollContainer,
  ]);

  const goNextClip = useCallback(() => {
    if (clipRanges.length === 0) return;
    setActiveClipIndex((activeClipIndex + 1) % clipRanges.length);
  }, [clipRanges.length, activeClipIndex, setActiveClipIndex]);

  const goPrevClip = useCallback(() => {
    if (clipRanges.length === 0) return;
    setActiveClipIndex(
      (activeClipIndex - 1 + clipRanges.length) % clipRanges.length,
    );
  }, [clipRanges.length, activeClipIndex, setActiveClipIndex]);

  // The transcript's one jump — cursor onto that element, then scroll it into
  // view. Called by nothing here: it exists for the `Edit Transcript` item on a
  // reel Section's context menu (PLAT-11061), which reaches this surface as a
  // window event the hook subscribes to. The hook says why it resolves by token
  // id rather than by the index the caller counted.
  useTranscriptIndexJump({
    videoCreator,
    transcriptElements,
    skipToWordWithIndex,
    setCursor,
    setAutoScroll,
    scrollableContainerRef,
    allowInitialScrollRef,
    setLastTimeUpdated,
  });

  // Shared by the pill's label and its dimmed nav arrows (PLAT-11039).
  const handleToggleClips = useCallback(() => {
    analytics.track('transcript_toggle_clips', {
      storyId: videoCreator.story?.id,
      storyTitle: videoCreator.story?.title,
    });
    if (!showClips) {
      setActiveClipIndex(getInitialVisibleIndex(clipRanges));
    }
    setShowClips(!showClips);
  }, [
    showClips,
    clipRanges,
    setActiveClipIndex,
    getInitialVisibleIndex,
    videoCreator.story,
  ]);

  // --- Overlay highlight logic (PLAT-10045 / PLAT-10066 / PLAT-10921) ---
  // Non-card composition placements ('overlay' | 'other' | 'quoteCard') get a
  // navy underline + hover popover, always on (no header toggle). Only the
  // clip-preview surface passes `clipTimelineMarkers`; `overlays` is undefined
  // everywhere else. Source-language ranges are the source of truth and are
  // computed first, then re-anchored into phrase-lead space for the translated
  // views, whose DOM is keyed by `data-initial-index` rather than `data-index`.
  // `TranslatedCaptionsView` renders neither attribute, so the apply pass
  // harmlessly finds zero spans there. Each helper below carries its own why:
  // which markers underline, how coverage treats deleted words, and how phrases
  // are best-fit assigned so two overlays can't merge into one underline.
  const overlayMarkers = clipTimelineMarkers?.overlays;
  const underlinedOverlayMarkers = useMemo(
    () => overlayMarkers?.filter(overlayShowsTranscriptUnderline),
    [overlayMarkers],
  );
  const isTranslatedTranscriptView = !!language;
  const overlaySpanAttr = isTranslatedTranscriptView
    ? 'data-initial-index'
    : 'data-index';
  const sourceOverlayRanges = useMemo(() => {
    if (!underlinedOverlayMarkers?.length || !transcriptElements?.length)
      return [];
    return findOverlayRanges(underlinedOverlayMarkers, transcriptElements);
  }, [underlinedOverlayMarkers, transcriptElements]);
  // `buildTranslatedPhraseTimeInfos` also runs inside `useTranslatedRender`.
  // Recomputing the lighter {lead, originalIndices} projection here beats
  // threading the hook's result up through props: both call sites memoize on
  // the same (translation, transcriptElements) inputs.
  const translatedPhraseInfos = useMemo(() => {
    if (!isTranslatedTranscriptView || !language) return [];
    const translation = translationStore.getTranslation(language);
    return buildTranslatedPhraseTimeInfos(translation, transcriptElements).map(
      p => ({ lead: p.lead, originalIndices: p.phrase.originalIndices }),
    );
  }, [
    isTranslatedTranscriptView,
    language,
    translationStore,
    transcriptElements,
  ]);
  const overlayRanges = useMemo(() => {
    if (sourceOverlayRanges.length === 0) return [];
    if (isTranslatedTranscriptView) {
      if (translatedPhraseInfos.length === 0) return [];
      return findTranslatedOverlayRanges(
        sourceOverlayRanges,
        translatedPhraseInfos,
      );
    }
    return sourceOverlayRanges;
  }, [sourceOverlayRanges, isTranslatedTranscriptView, translatedPhraseInfos]);

  // Highlight wash. Lives here rather than beside the quote/clip passes because
  // it needs the translated view's span key + phrase projection (PLAT-11192).
  useSavedSegmentWash({
    containerRef: scrollRef,
    ranges: filteredSavedSegmentRanges,
    colorByKey: savedSegmentColorByKey,
    spanAttr: overlaySpanAttr,
    translatedPhrases: isTranslatedTranscriptView
      ? translatedPhraseInfos
      : undefined,
    visibleParagraphIds: effectiveVisibleParagraphIds,
    blockSpanView: showHistory && !isTranslatedTranscriptView,
  });

  // Only the original-language regular view (per-word `data-index` spans) uses
  // the imperative pass below. The redline (TranscriptionDiffNew, block spans)
  // and both translated views (per-phrase spans with bare inter-phrase
  // whitespace) collapse text differently, so a per-span class can't make their
  // underlines continuous — they render underlines themselves, and hover/
  // brighten resolves from their `data-overlay-id` spans instead (see
  // overlayIdAttr / brightenOverlayUnderlinesById).
  const viewRendersOverlaysItself = showHistory || isTranslatedTranscriptView;
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    // Defensive top-of-body clear matching the quote/clip pattern above:
    // covers the (rare) case where the previous effect run's cleanup
    // didn't record (Suspense fallback transitions, strict-mode test
    // environments) so stale overlay-match classes don't accumulate.
    clearOverlayHighlights(container);
    if (!viewRendersOverlaysItself && overlayRanges.length > 0) {
      const allIndices = rangesToIndices(overlayRanges);
      applyOverlayHighlights(
        container,
        allIndices,
        null,
        getScrollContainer(),
        overlaySpanAttr,
      );
    }
    return () => {
      clearOverlayHighlights(container);
    };
  }, [
    overlayRanges,
    getScrollContainer,
    overlaySpanAttr,
    viewRendersOverlaysItself,
  ]);

  // --- Overlay range adjust (drag handles on the underline) ---
  // Original-language only — translated view spans are phrase-lead keyed, so
  // word-level snapping is meaningless there.
  const [adjustingOverlayId, setAdjustingOverlayId] = useState<string | null>(
    null,
  );
  const canAdjustOverlayRange =
    !!onAdjustOverlayRange && !isTranslatedTranscriptView;
  const onBeginAdjust = useMemo(
    () =>
      canAdjustOverlayRange
        ? (overlayId: string) => setAdjustingOverlayId(overlayId)
        : undefined,
    [canAdjustOverlayRange],
  );

  // --- Highlight hover popover (driven by render-prop from parent) ---
  // Only persisted clips are eligible for the popover — optimistic pending
  // ranges (PLAT-9793) have temp ids that won't resolve to a real clip.
  const highlightPopover = useHighlightHoverPopover({
    showQuotes: showQuotes && !!renderHighlightPopover,
    showClips: showClipsToggle && showClips && !!renderHighlightPopover,
    // Captions (karaoke) view draws no overlay underline — its `Word` omits
    // `overlayHighlightStyles` — so firing the overlay hover pill there is a
    // phantom affordance with no visual hint. Suppress it to match the missing
    // underline (PLAT-10961).
    showOverlays:
      overlayRanges.length > 0 && !!renderHighlightPopover && !isInKaraokeView,
    showSavedSegments: savedSegmentsEnabled && !!renderHighlightPopover,
    quoteRanges,
    clipRanges: realClipRanges,
    overlayRanges,
    savedSegmentRanges: filteredSavedSegmentRanges,
    getScrollContainer,
    renderPopover: renderHighlightPopover ?? (() => null),
    spanIndexAttr: overlaySpanAttr,
    // Redline collapses words into block spans, so overlays can't be resolved
    // by word index — resolve them from the `data-overlay-id` underline spans
    // the diff renders instead (see _TranscriptionDiff / findOverlayRanges).
    overlayIdAttr: viewRendersOverlaysItself ? 'data-overlay-id' : undefined,
    onBeginAdjust,
  });

  // The committed source-index range for the overlay being adjusted. Adjust is
  // gated to the original-language view, where `overlayRanges` is source space.
  const committedAdjustRange = useMemo<HighlightRange | null>(
    () =>
      adjustingOverlayId
        ? (sourceOverlayRanges.find(r => r.id === adjustingOverlayId) ?? null)
        : null,
    [adjustingOverlayId, sourceOverlayRanges],
  );

  // Exit adjust mode when the language flips to a translation, or the overlay
  // being adjusted no longer exists (deleted / range collapsed away).
  useEffect(() => {
    if (!adjustingOverlayId) return;
    if (isTranslatedTranscriptView || !committedAdjustRange) {
      setAdjustingOverlayId(null);
    }
  }, [adjustingOverlayId, isTranslatedTranscriptView, committedAdjustRange]);

  const handleCommitAdjust = useCallback(
    (overlayId: string, range: { start: number; end: number }) => {
      const els = transcriptElements as
        | ReadonlyArray<{
            ts?: number | null;
            end_ts?: number | null;
            initial_index?: number;
          }>
        | undefined;
      const startEl = els?.[range.start];
      const endEl = els?.[range.end];
      const time = typeof startEl?.ts === 'number' ? startEl.ts : null;
      const end = typeof endEl?.end_ts === 'number' ? endEl.end_ts : null;
      if (time == null || end == null || end <= time) return;
      // Clamped, never taken straight off the boundary words: a range dragged
      // across a reordered section boundary yields a source-index window that is
      // either inverted or covers a section the creator never touched.
      const anchor = clampAnchorToSelectionRun(els ?? [], {
        startIndex: range.start,
        endIndex: range.end,
      });
      onAdjustOverlayRange?.(overlayId, {
        startIndex: range.start,
        endIndex: range.end,
        time,
        duration: end - time,
        anchorStartInitialIndex: anchor?.startInitialIndex,
        anchorEndInitialIndex: anchor?.endInitialIndex,
      });
    },
    [onAdjustOverlayRange, transcriptElements],
  );

  const overlayAdjust = useOverlayRangeAdjust({
    adjustingOverlayId,
    committedRange: committedAdjustRange,
    transcriptElements: transcriptElements ?? [],
    getScrollContainer,
    spanIndexAttr: overlaySpanAttr,
    onCommit: handleCommitAdjust,
  });

  // PLAT-10045 / PLAT-10066 — brighten the overlay range under the cursor
  // (hover only). Tags spans inside the hovered range with
  // OVERLAY_MATCH_ACTIVE_CLASS; the CSS swaps the text-decoration-color to the
  // brighter `--content-overlays-hover`. Other overlay ranges stay navy.
  // Selection does NOT brighten: a just-added overlay is auto-selected, and
  // brightening on selection made it load looking "highlighted" — the
  // underline should only light up under the cursor. (Supersedes the
  // PLAT-10122 selection-brighten.)
  const hoveredOverlayId = highlightPopover.hoveredOverlayId;
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const activeIds = resolveActiveOverlayRangeIds(
      overlayRanges,
      undefined,
      hoveredOverlayId,
    );
    // Redline renders underlines as `OverlayUnderline` (data-overlay-id) spans,
    // not per-word `data-index` spans — brighten those directly by id. The
    // index-based pass would instead recolor a removed word's strikethrough
    // (owned by its block span). Other views keep the per-index brighten.
    if (viewRendersOverlaysItself) {
      brightenOverlayUnderlinesById(container, activeIds);
    } else {
      applyActiveOverlayBrighten(
        container,
        overlayRanges,
        activeIds,
        overlaySpanAttr,
      );
    }
    return () => clearActiveOverlayBrighten(container);
  }, [
    hoveredOverlayId,
    overlayRanges,
    overlaySpanAttr,
    viewRendersOverlaysItself,
  ]);

  // Overlay range-adjust live preview. Runs AFTER the base apply + active
  // effects (definition order = run order), so it wins for the overlay being
  // adjusted: while a handle is dragged it paints the tentative range
  // (preview) instead of the committed one, and brightens it. Overlays are
  // temporally disjoint, so toggling `overlay-match` off within this overlay's
  // contiguous [lo, hi] band never clears another overlay's underline.
  const previewAdjustRange = overlayAdjust.previewRange;
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !committedAdjustRange) return;
    const committed = committedAdjustRange;
    const effective = previewAdjustRange ?? {
      start: committed.start,
      end: committed.end,
    };
    const lo = Math.min(committed.start, effective.start);
    const hi = Math.max(committed.end, effective.end);
    for (let i = lo; i <= hi; i++) {
      const el = container.querySelector<HTMLElement>(
        `[${overlaySpanAttr}='${i}']`,
      );
      if (!el) continue;
      const inRange = i >= effective.start && i <= effective.end;
      el.classList.toggle(OVERLAY_MATCH_CLASS, inRange);
      el.classList.toggle(OVERLAY_MATCH_ACTIVE_CLASS, inRange);
    }
    return () => {
      // Restore the committed range's plain underline; drop the brighten.
      for (let i = lo; i <= hi; i++) {
        const el = container.querySelector<HTMLElement>(
          `[${overlaySpanAttr}='${i}']`,
        );
        if (!el) continue;
        const inCommitted = i >= committed.start && i <= committed.end;
        el.classList.toggle(OVERLAY_MATCH_CLASS, inCommitted);
        el.classList.remove(OVERLAY_MATCH_ACTIVE_CLASS);
      }
    };
  }, [committedAdjustRange, previewAdjustRange, overlaySpanAttr]);

  const handleReplace = () => {
    const { startIndex, endIndex } = getSelectedStartEndIndex(scrollRef) || {};
    if (startIndex === undefined || endIndex === undefined) return;
    setShowReplaceModal({ startIndex, endIndex });
  };

  const handleRemoveText = useCallback(() => {
    if (hideRemoveText || !isOriginalLang(language)) return;
    handleTranscriptDelete(
      cursor,
      setCursor,
      setLastTimeUpdated,
      scrollRef,
      isInKaraokeView,
    );
  }, [
    cursor,
    setCursor,
    setLastTimeUpdated,
    scrollRef,
    isInKaraokeView,
    hideRemoveText,
    language,
    // Memoized on `transcriptElements` — omitting it left Backspace calling
    // the pre-delete closure, so it deleted once and then no-opped forever
    // (PLAT-11558); nothing else refreshes this memo while paused.
    handleTranscriptDelete,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Let editable fields inside the transcript container (e.g. the
      // "Save Segment for Later" name prompt) own their own keys — otherwise
      // the transcript shortcuts below preventDefault Space/Backspace/etc.,
      // which breaks typing (spacebar wouldn't register in the input).
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.code === 'Backspace') {
        e.preventDefault();
        handleRemoveText();
      } else if (e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
        e.preventDefault();
        setAutoScroll(true);
        const direction: 1 | -1 = e.code === 'ArrowRight' ? 1 : -1;
        // Foreign-language views step phrase-by-phrase. Each press jumps
        // the cursor to the start of the next/prev phrase so the visual
        // marker advances on every press, instead of stepping by source
        // word (which produces no visual change for in-phrase advances).
        if (
          phraseStepIndices &&
          phraseStepIndices.length > 0 &&
          transcriptElements
        ) {
          let target: number | undefined;
          if (direction > 0) {
            target = phraseStepIndices.find(i => i > cursor.from);
          } else {
            for (let k = phraseStepIndices.length - 1; k >= 0; k--) {
              if (phraseStepIndices[k] < cursor.from) {
                target = phraseStepIndices[k];
                break;
              }
            }
          }
          if (target !== undefined) {
            setCursor({ from: target, to: target + 1 });
            const el = transcriptElements[target];
            if (el && 'ts' in el && typeof el.ts === 'number') {
              void videoCreator.setTime(Math.ceil(el.ts * 1000) / 1000, true);
            }
            return;
          }
        }
        handleCursorMove(direction, cursor, setCursor);
      } else if (e.code === 'Space' || e.code === 'KeyK') {
        // In clip-preview modals, play/pause is owned by ClipPreviewControlBar's
        // document-level keyup handler. Suppress the keydown path here so the
        // two don't fire on the same keypress and cancel each other out
        // depending on whether focus is inside the transcript scroll container.
        if (suppressPlayShortcut) return;
        e.preventDefault();
        videoCreator.isPlaying
          ? videoCreator.renderer?.pause()
          : videoCreator.refreshSourceAndPlay();
      } else if (e.code === 'Comma' || e.code === 'Period') {
        e.preventDefault();
        handleAddPunctuation(e.code, cursor, setCursor);
      } else if (e.code === 'Enter') {
        e.preventDefault();
        if (isInKaraokeView) {
          handleAddKaraokeBreak(cursor, e.shiftKey);
        } else {
          handleAddPunctuation(e.code, cursor, setCursor);
        }
      } else if (e.code === 'Backslash' && e.shiftKey) {
        e.preventDefault();
        handleAddKaraokeBreak(cursor);
      } else if (e.code === 'KeyS' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void saveService?.save(false);
      } else if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ' && !e.shiftKey) {
        e.preventDefault();
        // Undo speaker overrides first, then fall back to videoCreator undo
        if (speakerOverrides.hasUndo) {
          speakerOverrides.undo();
        } else {
          videoCreator.undo();
        }
      } else if (
        (e.ctrlKey || e.metaKey) &&
        (e.code === 'KeyY' || (e.shiftKey && e.code === 'KeyZ'))
      ) {
        e.preventDefault();
        if (speakerOverrides.hasRedo) {
          speakerOverrides.redo();
        } else {
          videoCreator.redo();
        }
      }
      e.stopImmediatePropagation();
      e.stopPropagation();
    },
    [
      handleRemoveText,
      handleCursorMove,
      handleAddPunctuation,
      handleAddKaraokeBreak,
      isInKaraokeView,
      videoCreator,
      cursor,
      setCursor,
      speakerOverrides,
      phraseStepIndices,
      transcriptElements,
      suppressPlayShortcut,
    ],
  );

  // todo make sure keyboard events from other places are not intercepted
  useEffect(() => {
    if (showReplaceModal || photoSelection) return;
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return function cleanup() {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, showReplaceModal, photoSelection]);

  const showOptionsForWordWithIndexAtPosition = useCallback(
    (
      index: number,
      positionXY: { x: number; y: number },
      selection: Selection | null,
    ) => {
      const built = getActions(index, selection, scrollRef);
      if (!built.actions.length) return;
      // Surface gates (hide flags, unwired handlers) drop rows this host never
      // offers; state-driven reasons ride along and disable rows in place.
      const actions = applyContextMenuSurfaceGates(built.actions, {
        hideAddPhoto,
        hideAddOverlayGraphic,
        hasOverlayHandler: !!onAddOverlayGraphicRequested,
        hideCutText,
        hideRemoveText,
        hideReplace,
        hideSaveAsClip,
        hasSavedSegmentService: !!savedSegmentService,
      });
      setSelectedWordIndex(index);
      setCursor({ from: index, to: index + 1 });
      setContextMenuPosition(positionXY);
      setContextMenuActions(actions);
      setContextMenuDisabledReasons(built.disabledReasons);
    },
    [
      getActions,
      isInKaraokeView,
      hideAddPhoto,
      hideAddOverlayGraphic,
      onAddOverlayGraphicRequested,
      hideCutText,
      hideRemoveText,
      hideReplace,
      hideSaveAsClip,
      savedSegmentService,
    ],
  );

  const mouseEnter = () => {
    // The renderer canvas mid-edit, or a field being typed in: see
    // `isFocusHeldElsewhere` for what each one costs.
    if (isFocusHeldElsewhere()) return;
    scrollRef.current?.focus();
  };

  const mouseLeave = () => {
    scrollRef.current?.blur();
    highlightPopover.onMouseLeave();
  };

  const showOptionsForSelection = useCallback(
    (
      e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
      selection: Selection,
      indices: { startIndex: number; endIndex: number },
    ) => {
      const { startIndex, endIndex } = indices;

      // Anchor the menu at the cursor. The menu clamps itself into the viewport
      // (see TranscriptContextMenu), so there's no need to derive a position
      // from the selection bounds — which, for tall or upward selections, lands
      // far from where the user actually clicked/released.
      const positionX = e.clientX;
      const positionY = e.clientY;

      if (endIndex !== undefined && endIndex > -1) {
        showOptionsForWordWithIndexAtPosition(
          endIndex,
          { x: positionX, y: positionY },
          selection,
        );
      } else if (startIndex !== undefined && startIndex > -1) {
        showOptionsForWordWithIndexAtPosition(
          startIndex,
          { x: positionX, y: positionY },
          selection,
        );
      }
      if (
        endIndex &&
        endIndex > -1 &&
        endIndex < transcriptElements!.length &&
        transcriptElements![endIndex].state !== 'removed' &&
        transcriptElements![endIndex].state !== 'cut'
      ) {
        // do not set cursor on removed
        // videoCreator.setTime(transcriptElements![endIndex]?.ts || 0);
        setCursor({ from: endIndex, to: endIndex + 1 });
      }
    },
    [showOptionsForWordWithIndexAtPosition, transcriptElements],
  );

  const handleMouseUp: React.MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      // Ignore events bubbling up from portaled descendants (e.g. the
      // Reassign Speaker modal). React routes synthetic events through the
      // component tree, not the DOM tree, so a mouseup inside a portaled
      // modal reaches this handler even though it renders outside the
      // transcript — re-showing the selection menu over the modal is wrong.
      if (!scrollRef.current?.contains(e.target as Node)) return;
      const fn = async () => {
        if (e.button !== 0) return;
        // hack to wait for selection to be reset properly, otherwise onMouseUp old selection is possibly captured
        await new Promise(resolve => setTimeout(resolve, 200));
        const selection = window.getSelection();
        if (!(selection && selection.toString().length)) return;
        const indices = getBoundaryIndexesFromSelection(selection, scrollRef);
        if (!indices) return;
        showOptionsForSelection(e, selection, indices);
      };
      fn();
    },
    [getBoundaryIndexesFromSelection, showOptionsForSelection, scrollRef],
  );

  const handleOpenPhotoModal = () => {
    // Translated context menu dispatches with a mapped source range instead
    // of a data-index DOM selection (PLAT-10646).
    const override = translatedRangeOverrideRef.current;
    if (override && transcriptElements) {
      togglePhotoSelectionModal({
        loc: override,
        text: extractTranscriptSelectionText(
          transcriptElements,
          override.startIndex,
          override.endIndex,
        ),
      });
      return;
    }
    const selection = window.getSelection();
    if (selection) {
      const boundaries = getBoundaryIndexesFromSelection(selection, scrollRef);
      const text = selection.toString();
      togglePhotoSelectionModal({ loc: boundaries!, text });
    }
  };

  // PLAT-10105 — resolve the current transcript selection to a timeline
  // range and hand it to the caller, which opens the filtered overlay
  // picker and inserts the chosen overlay bound to that range. Mirrors
  // `handleOpenPhotoModal`'s selection capture.
  const handleAddOverlayGraphicAction = () => {
    if (!onAddOverlayGraphicRequested) return;
    const selection = window.getSelection();
    // A missing DOM selection is fine when the translated context menu set
    // a programmatic range override (PLAT-10646).
    if (!selection && !translatedRangeOverrideRef.current) return;
    // Canonical selection resolution — the single path every transcript
    // action should use; normalizes to real words so the overlay range matches
    // the user's actual selection (no leading whitespace/preceding word).
    // Other actions migrate onto this in PLAT-10515.
    const resolved = resolveTranscriptSelection(selection, scrollRef);
    if (!resolved) {
      logger.warn('Add overlay graphic: unresolved selection time range');
      return;
    }
    // Anchor the overlay to its selected words by stable `initial_index` so it
    // follows the timeline through later edits — clamped, so a selection that
    // spans a reordered section boundary can't store a window covering words
    // outside it (see `clampAnchorToSelectionRun`).
    const anchor = clampAnchorToSelectionRun(
      (transcriptElements ?? []) as ReadonlyArray<{
        ts?: number | null;
        end_ts?: number | null;
        initial_index?: number;
      }>,
      { startIndex: resolved.startIndex, endIndex: resolved.endIndex },
    );
    onAddOverlayGraphicRequested({
      startTime: resolved.startTime,
      endTime: resolved.endTime,
      text: resolved.text,
      anchorStartInitialIndex: anchor?.startInitialIndex,
      anchorEndInitialIndex: anchor?.endInitialIndex,
    });
  };

  const handleAddPhotoWrapper = async () => {
    if (!photoSelection || !selectedImage?.url) {
      togglePhotoSelectionModal(null);
      return;
    }
    togglePhotoSelectionModal(null);
    await handleAddPhoto(photoSelection, selectedImage);
  };

  const handleSaveAsPullQuote = useCallback(async () => {
    // The DOM-selection gate only applies without a programmatic override —
    // the translated view has no data-index selection (PLAT-10646).
    if (!translatedRangeOverrideRef.current) {
      const selection = window.getSelection();
      if (!selection || !selection.toString().trim().length) return;
    }

    const { startIndex, endIndex } = getSelectedStartEndIndex(scrollRef) || {};
    if (startIndex == null || endIndex == null) return;

    const story = videoCreator.story;
    if (!story || !story.id || !transcriptElements) return;
    if (!savePullQuoteService) return;

    // Build selected text from transcript elements (whole words) rather than
    // the browser selection which may contain partial words
    const selectedText = extractTranscriptSelectionText(
      transcriptElements,
      startIndex,
      endIndex,
    );

    // Call service to handle business logic
    const result = await savePullQuoteService.savePullQuote({
      storyId: story.id,
      story,
      selectedText,
      startIndex,
      endIndex,
      transcriptElements,
      editorName: userIdentity.currentEditor,
    });

    if (!result.ok) {
      logger.error('Failed to save pull quote', result.error);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to save pull quote',
      });
      return;
    }

    const {
      savedId,
      speakerLabel,
      speakerId,
      storytellerName,
      quote,
      imagefile,
      metadata,
    } = result.value;

    const shareableImage = {
      id: savedId,
      quote,
      storytellerName,
      ...(speakerId
        ? { speaker: { id: speakerId, name: storytellerName ?? '' } }
        : {}),
      imagefile,
      metadata,
      _updatedAt: new Date().toISOString(),
    };

    pendingActiveQuoteIdRef.current = savedId;
    videoCreator.addShareableImage(shareableImage);
    if (!showQuotes && renderHighlightPopover) {
      setShowQuotes(true);
    }
    if (videoCreator.story) {
      await videoCreator.updateStory(videoCreator.story);
    }

    // Track analytics — PR4: migrated from Segment-only `analytics.track`
    // to EventTracker (PostHog + Datadog). Event name preserved for data
    // continuity.
    trackSavePullQuoteFromTranscript({
      action_source: surface,
      ...buildStoryVideoContext(videoCreator),
      ...buildSelectionTextStats(selectedText),
      speaker_label: speakerLabel,
      speaker_name: storytellerName,
      saved_id: savedId,
    });

    // Show success toast notification with deep link to Pull Quotes tab
    videoCreator.showToast({
      state: 'success',
      message: 'Pull Quote has been saved in the Content Studio',
      delay: 5000,
      action: {
        label: 'Open Pull Quote',
        onClick: () => {
          // Prompt if a clip save is still in-flight
          if (videoCreator.isBackgroundSaving) {
            const proceed = window.confirm(
              'Your clip is still saving. Leave now and open the Pull Quote?',
            );
            if (!proceed) return;
          }

          // PLAT-10948 — strip any clip-preview params riding along in the URL
          // so arriving on Pull Quotes doesn't also reopen a clip preview.
          const params = stripClipPreviewParams(searchParams);
          params.set('tab', ContentTab.pull_quotes);
          navigate({
            pathname: `/${Studio.content}`,
            search: params.toString(),
          });

          // Delay to allow Content Studio to mount/re-render before opening the quote
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('open-pull-quote', { detail: savedId }),
            );
          }, 300);
        },
      },
    });
  }, [
    getSelectedStartEndIndex,
    videoCreator,
    scrollRef,
    transcriptElements,
    savePullQuoteService,
    userIdentity.currentEditor,
    navigate,
    searchParams,
    showQuotes,
  ]);

  // Handle speaker label click - show context menu with speaker options
  const handleSpeakerClick = useCallback((info: SpeakerClickInfo) => {
    setSpeakerMenuInfo(info);
  }, []);

  // Close speaker context menu
  const closeSpeakerMenu = useCallback(() => {
    setSpeakerMenuInfo(null);
  }, []);

  // Handle "Reassign Speaker Everywhere" from context menu
  const handleReassignEverywhere = useCallback(async () => {
    if (!speakerMenuInfo) return;
    const storyId = videoCreator.story?.id;
    if (!storyId) return;

    let labelForReassign = speakerMenuInfo.speakerLabel;

    // If the section has a speaker override, recover the original voice label
    // for the global reassignment and stash the section indices so we can undo
    // the override when the user confirms the modal selection.
    if (!useOriginalTranscript && transcriptElements) {
      const indices = speakerMenuInfo.sectionIndices;
      const overriddenEl = indices
        .map(i => transcriptElements[i])
        .find(el => el && isElementReassigned(el));

      if (overriddenEl && overriddenEl.original_speaker_label) {
        labelForReassign = overriddenEl.original_speaker_label;
        setPendingOverrideUndo({ sectionIndices: indices });
      }
    }

    setReassignSpeakerLabel(labelForReassign);
    const result = await speakerService.fetchSpeakersForStory(storyId);
    if (result.ok) {
      setReassignSpeakerData(result.value);
      setShowReassignModal(true);
    } else {
      logger.error('Failed to fetch speakers for reassignment', result.error);
      setPendingOverrideUndo(null);
    }
  }, [
    speakerMenuInfo,
    videoCreator.story?.id,
    speakerService,
    useOriginalTranscript,
    transcriptElements,
  ]);

  // Handle "Reassign Selection Only" / "Assign Speaker for Selection Only" from context menu
  const handleReassignSectionOnly = useCallback(async () => {
    if (!speakerMenuInfo) return;
    const storyId = videoCreator.story?.id;
    if (!storyId) return;

    // Use the sectionIndices from the clicked speaker label to set selection range
    const indices = speakerMenuInfo.sectionIndices;
    if (!indices.length) return;

    const startIndex = Math.min(...indices);
    const endIndex = Math.max(...indices);
    setReassignFromSelection({ startIndex, endIndex });

    const result = await speakerService.fetchSpeakersForStory(storyId);
    if (result.ok) {
      setReassignSpeakerData(result.value);
      setShowReassignModal(true);
    } else {
      logger.error(
        'Failed to fetch speakers for section reassignment',
        result.error,
      );
      setReassignFromSelection(null);
    }
  }, [speakerMenuInfo, videoCreator.story?.id, speakerService]);

  // Handle "Undo Selection Reassign" from speaker label context menu
  const handleUndoSectionReassign = useCallback(() => {
    if (!speakerMenuInfo) return;
    const indices = speakerMenuInfo.sectionIndices;
    if (!indices.length) return;

    if (useOriginalTranscript) {
      speakerOverrides.undoSectionReassign(indices);
    } else {
      const startIndex = Math.min(...indices);
      const endIndex = Math.max(...indices);
      const segBounds = convertIndexToSegmentBoundaries({
        startIndex,
        endIndex,
      });
      if (!segBounds) return;
      editorStore.editor?.commandUndoReassignSpeakerSection(
        segBounds.fromSegmentId,
        segBounds.fromIndex,
        segBounds.toSegmentId,
        segBounds.toIndex,
      );
    }
  }, [
    speakerMenuInfo,
    speakerOverrides,
    useOriginalTranscript,
    convertIndexToSegmentBoundaries,
    editorStore.editor,
  ]);

  // Handle "Unassign Speaker" from context menu
  const handleUnassignSpeaker = useCallback(async () => {
    if (!speakerMenuInfo) return;
    const storyId = videoCreator.story?.id;
    if (!storyId) return;

    const result = await speakerService.unassignSpeaker(
      storyId,
      speakerMenuInfo.speakerLabel,
    );
    if (result.ok) {
      // Write-through: revert cached label to fallback name/color so the
      // transcript UI updates immediately without waiting for a re-fetch.
      cacheAndWriteSpeakerAssignment(
        storyId,
        speakerMenuInfo.speakerLabel,
        getSpeakerNameFromLabel(speakerMenuInfo.speakerLabel),
        getSpeakerColorFromLabel(speakerMenuInfo.speakerLabel),
      );
      notifySpeakerCacheUpdated(storyId);
    } else {
      logger.error('Failed to unassign speaker', result.error);
    }
  }, [speakerMenuInfo, videoCreator.story?.id, speakerService]);

  // Handle selecting a speaker in the reassign modal
  const handleReassignSpeaker = useCallback(
    async (datoSpeakerId: string): Promise<boolean> => {
      const storyId = videoCreator.story?.id;
      if (!storyId) return false;

      // Selection-based reassignment: assign speaker to highlighted text section
      if (reassignFromSelection && transcriptElements && reassignSpeakerData) {
        const { startIndex, endIndex } = reassignFromSelection;

        // Find the existing speaker label for this Dato speaker from voice mappings.
        const currentMappings = reassignSpeakerData.mappings || {};

        // 1. Look up in voice mappings
        let newLabel = Object.entries(currentMappings).find(
          ([, id]) => id === datoSpeakerId,
        )?.[0];
        // 2. If not found in mappings, scan transcript elements for a label.
        //    Only for original transcript mode — video-level elements may have
        //    stale/inconsistent speaker_id values from prior overrides.
        if (!newLabel && useOriginalTranscript && transcriptElements) {
          const matchingEl = transcriptElements.find(
            el => el.type === 'text' && el.speaker_id === datoSpeakerId,
          );
          if (matchingEl?.speaker_label) {
            newLabel = matchingEl.speaker_label;
          }
        }

        // 3. Last resort: generate an override label
        if (!newLabel) {
          newLabel = `override_${datoSpeakerId}`;
        }

        if (useOriginalTranscript) {
          speakerOverrides.reassignSection(
            startIndex,
            endIndex,
            newLabel,
            datoSpeakerId,
            reassignSpeakerData,
          );
        } else {
          const segBounds = convertIndexToSegmentBoundaries({
            startIndex,
            endIndex,
          });
          if (!segBounds) {
            logger.error(
              'Failed to convert indices to segment boundaries for reassignment',
            );
            setShowReassignModal(false);
            setReassignFromSelection(null);
            setReassignSpeakerData(null);
            return false;
          }
          editorStore.editor?.commandReassignSpeakerSection(
            segBounds.fromSegmentId,
            segBounds.fromIndex,
            segBounds.toSegmentId,
            segBounds.toIndex,
            newLabel,
          );

          // Inject into speaker cache for immediate name resolution
          // (mirrors what useSpeakerOverrides.reassignSection does internally)
          const allSpeakersForCache = [
            ...(reassignSpeakerData?.storySpeakers || []),
            ...(reassignSpeakerData?.orgSpeakers || []),
          ];
          const selectedSpeaker = allSpeakersForCache.find(
            s => s.id === datoSpeakerId,
          );
          if (selectedSpeaker && storyId) {
            injectSpeakerLabel(
              storyId,
              newLabel,
              selectedSpeaker.name || 'Unknown',
              getSpeakerColorFromDatoId(datoSpeakerId, 0),
            );
          }
        }

        // PLAT-9178: Auto-add org speaker to story if not already present
        if (datoClientStore?.speakerRepository) {
          try {
            await datoClientStore.speakerRepository.addSpeakerReferenceToStory(
              datoSpeakerId,
              storyId,
            );
            // Optimistic panel update FIRST — before cache invalidation triggers CDN re-fetch
            const allSpeakers = [
              ...(reassignSpeakerData?.storySpeakers || []),
              ...(reassignSpeakerData?.orgSpeakers || []),
            ];
            const matched = allSpeakers.find(
              (s: { id: string }) => s.id === datoSpeakerId,
            );
            if (matched) {
              notifySpeakerAddedToStory(storyId, {
                ...matched,
                name: matched.name || 'Unknown',
                color: getSpeakerColorFromLabel(newLabel),
              });
            }
            clearSpeakerUtilsCache();
            notifySpeakerCacheUpdated(storyId);
          } catch (err) {
            logger.warn(
              'Auto-add speaker to story failed on section reassign',
              err,
            );
          }
        }

        setShowReassignModal(false);
        setReassignFromSelection(null);
        setReassignSpeakerData(null);
        return true;
      }

      // Global reassignment path: remap speaker label to a different Dato speaker
      if (!reassignSpeakerLabel || !reassignSpeakerData) return false;

      // Optimistic panel update BEFORE the await — assignSpeaker() fires
      // speaker-cache-invalidated internally which triggers a CDN re-fetch.
      const allSpeakers = [
        ...(reassignSpeakerData?.storySpeakers || []),
        ...(reassignSpeakerData?.orgSpeakers || []),
      ];
      const matched = allSpeakers.find(
        (s: { id: string }) => s.id === datoSpeakerId,
      );
      // Find who was previously mapped to this label (will be moved to unassigned)
      const previousId = reassignSpeakerData.mappings?.[reassignSpeakerLabel];
      if (matched && storyId) {
        notifySpeakerAddedToStory(
          storyId,
          {
            ...matched,
            name: matched.name || 'Unknown',
            color: getSpeakerColorFromDatoId(matched.id, 0),
          },
          previousId !== datoSpeakerId ? previousId : undefined,
          reassignSpeakerLabel || undefined,
        );
      }

      // If user initiated global reassign from an overridden section,
      // undo the section override now that they've confirmed a selection.
      if (pendingOverrideUndo) {
        const { sectionIndices } = pendingOverrideUndo;
        const startIndex = Math.min(...sectionIndices);
        const endIndex = Math.max(...sectionIndices);
        const segBounds = convertIndexToSegmentBoundaries({
          startIndex,
          endIndex,
        });
        if (segBounds) {
          editorStore.editor?.commandUndoReassignSpeakerSection(
            segBounds.fromSegmentId,
            segBounds.fromIndex,
            segBounds.toSegmentId,
            segBounds.toIndex,
          );
        }
        setPendingOverrideUndo(null);
      }

      const result = await speakerService.assignSpeaker(
        storyId,
        reassignSpeakerLabel,
        datoSpeakerId,
        reassignSpeakerData.mappings,
      );

      setShowReassignModal(false);
      setReassignSpeakerLabel(null);
      setReassignSpeakerData(null);

      if (!result.ok) {
        logger.error('Failed to reassign speaker', result.error);
        return false;
      }

      // Write the new mapping to the label cache so the transcript resolves immediately.
      if (matched && reassignSpeakerLabel) {
        cacheAndWriteSpeakerAssignment(
          storyId,
          reassignSpeakerLabel,
          matched.name || 'Unknown',
          getSpeakerColorFromDatoId(matched.id, 0),
        );
        notifySpeakerCacheUpdated(storyId);
      }

      return true;
    },
    [
      videoCreator.story?.id,
      reassignSpeakerLabel,
      reassignSpeakerData,
      reassignFromSelection,
      transcriptElements,
      speakerService,
      speakerOverrides,
      datoClientStore?.speakerRepository,
      pendingOverrideUndo,
      convertIndexToSegmentBoundaries,
      editorStore.editor,
    ],
  );

  // Handle "Create New Speaker" from reassign context
  const handleCreateNewFromTranscript = useCallback(() => {
    setShowReassignModal(false);
    setShowCreateSpeakerModal(true);
  }, []);

  const handleCreateSpeakerFromTranscript = useCallback(
    async (speakerData: SpeakerData): Promise<boolean> => {
      const storyId = videoCreator.story?.id;
      const albumId = videoCreator.story?.primaryShowcase?.id;
      if (!storyId) return false;

      // Section/selection-based create+assign: create the speaker without a
      // global voice mapping, then assign it to the highlighted section via an
      // override (mirrors the selection path of handleReassignSpeaker). Here
      // reassignSpeakerLabel is null, so the global path below would bail out.
      if (reassignFromSelection && reassignSpeakerData) {
        const { startIndex, endIndex } = reassignFromSelection;

        // Resolve segment boundaries for the scoped-editor path up front, so a
        // conversion failure aborts BEFORE we create the speaker — otherwise the
        // speaker would be persisted in DatoCMS with no section assignment (an
        // orphan). The original-transcript path assigns by raw index and does
        // not need segment boundaries.
        let segBounds: ReturnType<typeof convertIndexToSegmentBoundaries> =
          null;
        if (!useOriginalTranscript) {
          segBounds = convertIndexToSegmentBoundaries({ startIndex, endIndex });
          if (!segBounds) {
            logger.error(
              'Failed to convert indices to segment boundaries for reassignment',
              { startIndex, endIndex },
            );
            setShowCreateSpeakerModal(false);
            setReassignFromSelection(null);
            setReassignSpeakerData(null);
            setReassignSpeakerLabel(null);
            return false;
          }
        }

        const createResult = await speakerService.createAndAssignSpeaker(
          storyId,
          albumId,
          '', // no global voice mapping — section override only
          speakerData,
          reassignSpeakerData.mappings || {},
        );
        if (!createResult.ok) {
          logger.error('Failed to create speaker', createResult.error);
          return false;
        }

        const newSpeaker = createResult.value;
        const datoSpeakerId = newSpeaker.id;
        // New speakers have no voice mapping, so always use an override label.
        const newLabel = `override_${datoSpeakerId}`;

        // Enrich the speaker lists so cache injection can resolve the new name.
        const enrichedData = {
          ...reassignSpeakerData,
          storySpeakers: [
            ...(reassignSpeakerData.storySpeakers || []),
            {
              ...newSpeaker,
              color: getSpeakerColorFromDatoId(datoSpeakerId, 0),
            },
          ],
        };

        if (useOriginalTranscript) {
          speakerOverrides.reassignSection(
            startIndex,
            endIndex,
            newLabel,
            datoSpeakerId,
            enrichedData,
          );
        } else if (segBounds) {
          editorStore.editor?.commandReassignSpeakerSection(
            segBounds.fromSegmentId,
            segBounds.fromIndex,
            segBounds.toSegmentId,
            segBounds.toIndex,
            newLabel,
          );
          injectSpeakerLabel(
            storyId,
            newLabel,
            newSpeaker.name || 'Unknown',
            getSpeakerColorFromDatoId(datoSpeakerId, 0),
          );
        }

        // Optimistically move the new speaker into the speakers panel's
        // Assigned section — the CDA re-fetch won't include it yet. Fired
        // before cache invalidation so the panel suppresses the stale re-fetch.
        // Built from the form data: `newSpeaker` is the raw CMA item, whose
        // headshots aren't in the { id, url } shape the panel renders.
        notifySpeakerAddedToStory(storyId, {
          ...speakerData,
          id: datoSpeakerId,
          name: newSpeaker.name || 'Unknown',
          gender: speakerData.gender || null,
          headshots: speakerData.headshots ?? undefined,
          color: getSpeakerColorFromLabel(newLabel),
        });

        notifySpeakerCacheUpdated(storyId);
        setShowCreateSpeakerModal(false);
        setReassignFromSelection(null);
        setReassignSpeakerData(null);
        setReassignSpeakerLabel(null);
        return true;
      }

      // Global reassignment path: remap a voice label to the new speaker.
      if (!reassignSpeakerLabel) return false;

      const result = await speakerService.createAndAssignSpeaker(
        storyId,
        albumId,
        reassignSpeakerLabel,
        speakerData,
        reassignSpeakerData?.mappings || {},
      );

      if (result.ok) {
        // Optimistic cache update so the transcript shows the new name immediately
        if (reassignSpeakerLabel && storyId) {
          cacheAndWriteSpeakerAssignment(
            storyId,
            reassignSpeakerLabel,
            speakerData.name,
            getSpeakerColorFromDatoId(result.value.id, 0),
          );
          notifySpeakerCacheUpdated(storyId);
        }
        setShowCreateSpeakerModal(false);
        setReassignSpeakerLabel(null);
        setReassignSpeakerData(null);
        return true;
      }
      logger.error('Failed to create speaker', result.error);
      return false;
    },
    [
      videoCreator.story?.id,
      videoCreator.story?.primaryShowcase?.id,
      reassignSpeakerLabel,
      reassignSpeakerData,
      reassignFromSelection,
      speakerService,
      speakerOverrides,
      useOriginalTranscript,
      convertIndexToSegmentBoundaries,
      editorStore.editor,
    ],
  );

  const closeReassignState = useCallback(() => {
    setShowReassignModal(false);
    setShowCreateSpeakerModal(false);
    setReassignSpeakerLabel(null);
    setReassignSpeakerData(null);
    setPendingOverrideUndo(null);
  }, []);

  const contextMenuCallback = async (
    action: TranscriptContextMenuActions | 'close',
  ) => {
    setContextMenuPosition(null);
    if (action === 'replace') {
      handleReplace();
    } else if (action === 'hideKaraoke') {
      handleHideKaraoke(scrollRef);
    } else if (action === 'addLineBreak' || action === 'removeLineBreak') {
      handleToggleCaptionSoftBreak(selectedWordIndex);
    } else if (action === 'removeText') {
      await handleRemoveText();
    } else if (action === 'removePhoto') {
      await handleRemovePhoto();
    } else if (action === 'saveAsClip') {
      const tempId = `pending-clip-${Date.now()}`;
      // Capture selected text BEFORE the save call so the browser selection
      // can't get cleared underneath us during the awaited save.
      const clipBounds = getSelectedStartEndIndex(scrollRef);
      const clipSelectedText = clipBounds
        ? extractTranscriptSelectionText(
            transcriptElements,
            clipBounds.startIndex,
            clipBounds.endIndex,
          )
        : '';
      try {
        const saved = await handleSaveAsClip(
          scrollRef,
          ({ startIndex, endIndex }) => {
            const newRange = {
              id: tempId,
              start: startIndex,
              end: endIndex,
            };
            setPendingClipRanges(prev => {
              const next = [...prev, newRange];
              const realRanges = findClipRanges(otherVideos || []);
              const realKeys = new Set(
                realRanges.map(r => `${r.start}-${r.end}`),
              );
              const merged = [
                ...realRanges,
                ...next.filter(r => !realKeys.has(`${r.start}-${r.end}`)),
              ].sort((a, b) => a.start - b.start);
              const newIdx = merged.findIndex(r => r.id === tempId);
              if (newIdx >= 0) setActiveClipIndex(newIdx);
              return next;
            });
            setShowClips(true);
          },
          onOpenClip,
        );
        if (saved && clipSelectedText) {
          trackSaveClipFromTranscript({
            action_source: surface,
            ...buildStoryVideoContext(videoCreator),
            ...buildSelectionTextStats(clipSelectedText),
          });
        }
      } finally {
        setPendingClipRanges(prev => prev.filter(r => r.id !== tempId));
      }
    } else if (action === 'saveAsPullQuote') {
      await handleSaveAsPullQuote();
    } else if (action === 'addPhoto') {
      handleOpenPhotoModal();
    } else if (action === 'addOverlayGraphic') {
      handleAddOverlayGraphicAction();
    } else if (action === 'restore' || action === 'showKaraoke') {
      await handleTranscriptRestore(action, scrollRef);
    } else if (action === 'cutText') {
      await handleCutText(
        cursor,
        setCursor,
        setLastTimeUpdated,
        scrollRef,
        isInKaraokeView,
      );
    } else if (action === 'pasteTextAfter' || action === 'pasteTextBefore') {
      handlePasteText(action === 'pasteTextAfter', scrollRef);
    } else if (action === 'reassignSpeaker') {
      const indices = getSelectedStartEndIndex(scrollRef);
      if (indices) {
        const storyId = videoCreator.story?.id;
        if (storyId) {
          setReassignFromSelection(indices);
          const result = await speakerService.fetchSpeakersForStory(storyId);
          if (result.ok) {
            setReassignSpeakerData(result.value);
            setShowReassignModal(true);
          } else {
            logger.error(
              'Failed to fetch speakers for selection reassignment',
              result.error,
            );
            setReassignFromSelection(null);
          }
        }
      }
    } else if (action === 'undoReassignSpeaker') {
      const indices = getSelectedStartEndIndex(scrollRef);
      if (indices) {
        if (useOriginalTranscript) {
          const allIndices: number[] = [];
          for (let i = indices.startIndex; i <= indices.endIndex; i++)
            allIndices.push(i);
          speakerOverrides.undoSectionReassign(allIndices);
        } else {
          const segBounds = convertIndexToSegmentBoundaries(indices);
          if (segBounds) {
            editorStore.editor?.commandUndoReassignSpeakerSection(
              segBounds.fromSegmentId,
              segBounds.fromIndex,
              segBounds.toSegmentId,
              segBounds.toIndex,
            );
          }
        }
      }
    }
    if (action !== 'replace') {
      setSelectedWordIndex(-1);
    }
  };

  const closeContextMenu = () => {
    setContextMenuPosition(null);
    setContextMenuActions(null);
    setSelectedWordIndex(-1);
  };

  // Create a new highlight for the current selection in the chosen color.
  // The creator picks a color from the context-menu swatches; the highlight is
  // named for that color ("Violet Highlight") rather than the selected text —
  // the text shows on row hover instead (rename is a secondary action in the
  // hover popover). Save runs in the background — the toast reports the outcome.
  const handleCreateNewSegment = (color: string) => {
    const bounds = getSelectedStartEndIndex(scrollRef);
    if (!bounds || !savedSegmentService) return;
    const name = savedSegmentDefaultName(color);
    const { startIndex, endIndex } = bounds;
    const service = savedSegmentService;
    closeContextMenu();
    void (async () => {
      const key = await service.saveSegment({
        name,
        startIndex,
        endIndex,
        color,
      });
      videoCreator.showToast(
        key != null
          ? {
              state: 'success',
              message: `${name} saved`,
            }
          : {
              state: 'error',
              message: 'Failed to save highlight. Please try again.',
            },
      );
    })();
  };

  // Append the current selection to an existing segment group (submenu item).
  const handleSaveToSegment = async (key: string) => {
    if (!savedSegmentService) return;
    const bounds = getSelectedStartEndIndex(scrollRef);
    closeContextMenu();
    if (!bounds) return;
    const name = savedSegmentsMap?.[Number(key)]?.name ?? 'segment';
    await savedSegmentService.addRangeToSegment({
      key,
      startIndex: bounds.startIndex,
      endIndex: bounds.endIndex,
    });
    videoCreator.showToast({
      state: 'success',
      message: `Added to "${name}"`,
    });
  };

  // Rename an existing highlight group from the Highlights dropdown's per-row
  // menu — the only entry point since PLAT-11182 retired the context-menu
  // kebab, so there is no context menu left to close here.
  const handleRenameSegment = async (key: string, name: string) => {
    if (!savedSegmentService) return;
    await savedSegmentService.renameSegment(key, name);
    videoCreator.showToast({
      state: 'success',
      message: `Highlight renamed to "${name}"`,
    });
  };

  // Delete a highlight group from the Highlights dropdown's per-row menu
  // (PLAT-11182). The dropdown confirms first — this is the destructive step.
  const handleRemoveSegment = async (key: string) => {
    if (!savedSegmentService) return;
    const name = savedSegmentTargets.find(t => t.key === key)?.name;
    await savedSegmentService.removeSegment(key);
    videoCreator.showToast({
      state: 'success',
      message: `Deleted "${name ?? 'highlight'}"`,
    });
  };

  // PLAT-10646 — dispatcher for the translated prose view's context menu.
  // Sets the mapped source range for the duration of one dispatch so the
  // hook's selection resolvers (and the handlers' DOM-selection gates)
  // consume the translated selection instead of a data-index DOM range.
  // Identity-stable (latest callback via render-mirrored ref) so it doesn't
  // bust the memoized TranscriptContent.
  const contextMenuCallbackRef = useRef(contextMenuCallback);
  contextMenuCallbackRef.current = contextMenuCallback;
  const handleTranslatedContextAction = useCallback(
    async (
      action: TranscriptContextMenuActions,
      range: { startIndex: number; endIndex: number },
    ) => {
      translatedRangeOverrideRef.current = range;
      try {
        await contextMenuCallbackRef.current(action);
      } finally {
        translatedRangeOverrideRef.current = null;
      }
    },
    [],
  );

  // PLAT-10646 / PLAT-11599 — the translated prose view's menu after Fix
  // Translation: Remove / Cut Text listed but disabled with a reason, the
  // rest gated by the same per-surface hide flags as the original menu.
  const {
    actions: translatedContextMenuActions,
    disabledReasons: translatedContextMenuDisabledReasons,
  } = useTranslatedContextMenuActions({
    hideReplace,
    hideRemoveText,
    hideCutText,
    hideSaveAsClip,
    hideAddPhoto,
    hideAddOverlayGraphic,
    hasOverlayHandler: !!onAddOverlayGraphicRequested,
    addedPunchListItemId: videoCreator.punchListManager.addedPunchListItemId,
  });

  const handleContextMenu: React.MouseEventHandler<HTMLDivElement> =
    useCallback(
      e => {
        // Portaled descendants (e.g. the Reassign Speaker modal) bubble their
        // events through the React tree to this handler; ignore them so the
        // transcript menu doesn't reappear over a modal (and so we don't
        // suppress the modal's own native context menu).
        if (!scrollRef.current?.contains(e.target as Node)) return;
        e.preventDefault();
        const selection = window.getSelection();
        if (selection && selection.type === 'Range') {
          const indices = getBoundaryIndexesFromSelection(selection, scrollRef);
          if (indices) showOptionsForSelection(e, selection, indices);
          return;
        }

        let index: number | undefined;

        // In diff view, data-index on the block span is always the block's first
        // element. Use caretPositionFromPoint to resolve the precise word.
        if (showHistory) {
          const match = resolveBlockElementAtPoint(
            e.clientX,
            e.clientY,
            splitTranscriptBlocks,
          );
          if (match) {
            index = match.block.indexOffset + match.elementIndex;
          }
        }

        // Fallback for non-diff views or if point resolution failed
        if (index === undefined) {
          const rawIndex = Number((e.target as HTMLElement).dataset.index);
          if (!isNaN(rawIndex)) index = rawIndex;
        }

        if (index !== undefined) {
          showOptionsForWordWithIndexAtPosition(
            index,
            { x: e.clientX, y: e.clientY },
            selection,
          );
        }
      },
      [
        getBoundaryIndexesFromSelection,
        showOptionsForSelection,
        showOptionsForWordWithIndexAtPosition,
        showHistory,
        splitTranscriptBlocks,
        scrollRef,
      ],
    );

  const longPressDuration = 600;
  const touchTolerance = 10;
  const touchData = React.useRef<{
    timerId: ReturnType<typeof setTimeout> | null;
    startX: number;
    startY: number;
  }>({
    timerId: null,
    startX: 0,
    startY: 0,
  });

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    touchData.current.startX = touch.pageX;
    touchData.current.startY = touch.pageY;
    touchData.current.timerId = setTimeout(() => {
      touchData.current.timerId = null;

      // Prevent native menu
      e.preventDefault();

      // Simulate context menu triggering
      const fakeEvent = {
        preventDefault: () => {},
        target: document.elementFromPoint(
          touch.pageX - window.pageXOffset,
          touch.pageY - window.pageYOffset,
        ),
        clientX: touch.clientX,
        clientY: touch.clientY,
      } as unknown as React.MouseEvent<HTMLDivElement>;
      handleContextMenu(fakeEvent);
    }, longPressDuration);
  };

  const onTouchEnd = () => {
    if (touchData.current.timerId) {
      clearTimeout(touchData.current.timerId);
      touchData.current.timerId = null;
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setAutoScroll(false);
    if (!touchData.current.timerId) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.pageX - touchData.current.startX);
    const deltaY = Math.abs(touch.pageY - touchData.current.startY);
    if (deltaX > touchTolerance || deltaY > touchTolerance) {
      clearTimeout(touchData.current.timerId);
      touchData.current.timerId = null;
    }
  };

  // Shared word-click handler: seek playhead to the given element index.
  // Used by the Main-level onClick (for per-word views with `data-index` on each word)
  // and by the diff view's block-level handler (which resolves the clicked element
  // via caret position before invoking this).
  const handleWordClick = useCallback(
    (index: number) => {
      setLastTimeUpdated(0);
      skipToWordWithIndex(index, setCursor, setAutoScroll);
      // Dispatch seek event so TranscriptVideoPlayer can seek directly
      // (its RAF-based feedback prevention blocks the MobX reaction during
      // playback). Scoped to OUR videoCreator: a word click inside a
      // clip-scoped review pane must not move players on other surfaces
      // (PLAT-10286).
      const el = transcriptElements?.[index];
      if (el && 'ts' in el && el.ts != null) {
        dispatchTranscriptSeek(el.ts, videoCreator);
      }
    },
    [skipToWordWithIndex, transcriptElements, videoCreator],
  );

  const handleReplaceTextSubmit = (
    selections: {
      startIndex: number;
      endIndex: number;
      newValue: string;
      retainState?: boolean;
    }[],
  ) => {
    if (selections.length && transcriptElements) {
      const first = selections[0];
      trackReplaceTranscriptText({
        action_source: surface,
        ...buildStoryVideoContext(videoCreator),
        original_text: extractTranscriptSelectionText(
          transcriptElements,
          first.startIndex,
          first.endIndex,
        ),
        replacement_text: first.newValue,
        is_multi_range: selections.length > 1,
        match_count: selections.length,
      });
    }
    handleTranscriptTextReplace(selections);
  };

  const redlineView = useRedlineToggle({
    captureCursorYLock,
    setShowHistory,
    videoCreator,
    isOn: showHistory,
    blocked: showOnlyActive,
    isRedlineSurface: !!clipTimelineMarkers,
  });
  const overflowItems = useTranscriptOverflowItems({
    videoCreator,
    transcriptElements,
    getSpeakerName: resolvedSpeakerName,
    language,
    saveService,
    showSubtitles,
    hasOriginalTranscription: !!originalTranscription,
    hideCaptions,
    isInKaraokeView,
    onOpenDebug: () => setDebugEnabled(true),
  });

  return (
    <Main
      $compact={hideHeader}
      id={'transcript-container'}
      ref={scrollRef}
      tabIndex={-1}
      onWheel={() => {
        setAutoScroll(false);
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={e => {
        const index = Number((e.target as HTMLElement).dataset.index);
        if (!isNaN(index)) {
          handleWordClick(index);
        }
      }}
      onContextMenu={handleContextMenu}
      onMouseDown={e => {
        highlightPopover.onMouseDown(e);
      }}
      onMouseMove={e => {
        highlightPopover.onMouseMove(e);
      }}
      onMouseUp={e => {
        highlightPopover.onMouseUp(e);
        handleMouseUp(e);
      }}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      {/* Hide the hover pill while the selection context menu (Replace /
          Remove / Cut) is open — the two float independently and otherwise
          overlap, hiding the menu's actions (PLAT-10961). */}
      {!contextMenuPosition && highlightPopover.renderOverlay()}
      {adjustingOverlayId &&
        committedAdjustRange &&
        !isTranslatedTranscriptView && (
          <OverlayRangeHandles
            range={overlayAdjust.previewRange ?? committedAdjustRange}
            getScrollContainer={getScrollContainer}
            spanIndexAttr={overlaySpanAttr}
            activeHandle={overlayAdjust.activeHandle}
            onBeginDrag={overlayAdjust.beginDrag}
            onNudge={overlayAdjust.nudgeBoundary}
            onExit={() => setAdjustingOverlayId(null)}
          />
        )}
      {contextMenuPosition && contextMenuActions && selectedWordIndex > -1 && (
        <TranscriptContextMenu
          positionXY={contextMenuPosition}
          actions={contextMenuActions}
          disabledReasons={contextMenuDisabledReasons}
          actionCallback={contextMenuCallback}
          savedSegmentTargets={savedSegmentTargets}
          onSaveToSegment={key => void handleSaveToSegment(key)}
          onCreateNewSegment={handleCreateNewSegment}
        />
      )}
      {showReplaceModal && (
        <WordReplacementModal
          selection={showReplaceModal}
          transcriptElements={transcriptElements || []}
          discard={() => {
            setShowReplaceModal(null);
            setSelectedWordIndex(-1);
          }}
          onSubmit={handleReplaceTextSubmit}
          onAddToDictionary={addTermToCompanyDictionary}
        />
      )}
      {showReplaceWarning && !showSubtitles && (
        <RegenerateTranscriptionModal
          type={'transcription'}
          confirm={async () => {
            transcriptionStore.regenerateTranscription();
            setShowReplaceWarning(false);
          }}
          discard={() => {
            setShowReplaceWarning(false);
          }}
        />
      )}
      {showReplaceWarning && showSubtitles && (
        <RegenerateTranscriptionModal
          type={'karaoke breaks'}
          confirm={async () => {
            commandProduceKaraoke({ replaceTrack: true });
            setShowReplaceWarning(false);
          }}
          discard={() => {
            setShowReplaceWarning(false);
          }}
        />
      )}
      <Header data-testid="transcript-header" $topicGutter={topicGutter}>
        <Buttons>
          <LeftSideButtons>
            {!hideHeader && (
              <>
                {originalTranscription && !hideCaptions && (
                  <TranscriptModeToggle
                    captionsView={isInKaraokeView}
                    videoCreator={videoCreator}
                    captionsAvailable={hasKaraokeProduced}
                    forceCaptionsView={forceCaptionsView}
                    redlines={
                      enableTranslationReviewerMode ? undefined : redlineView
                    }
                  />
                )}
                <SearchAnchor>
                  <PanelButton
                    tabIndex={-1}
                    onFocus={() => scrollRef.current!.focus()}
                    onClick={() => setIsSearchOpen(prev => !prev)}
                    isPressed={isSearchOpen}
                    data-testid="transcript-search-input"
                    data-tooltip-id="search-tooltip"
                    data-tooltip-content={
                      isSearchOpen ? 'Close search' : 'Search transcript'
                    }
                  >
                    <SearchIconOutline />
                  </PanelButton>
                  {isSearchOpen && (
                    <FloatingSearch>
                      <TranscriptSearch
                        transcriptElements={transcriptElements}
                        scrollRef={scrollRef}
                        includeRemovedElements={showHistory}
                        language={language}
                        onClose={() => setIsSearchOpen(false)}
                      />
                    </FloatingSearch>
                  )}
                </SearchAnchor>
                {showUndoRedo && (
                  <UndoRedoButtons
                    canUndo={canUndoState || speakerOverrides.hasUndo}
                    canRedo={canRedoState || speakerOverrides.hasRedo}
                    onUndo={() => {
                      if (speakerOverrides.hasUndo) {
                        speakerOverrides.undo();
                      } else {
                        videoCreator.undo();
                      }
                    }}
                    onRedo={() => {
                      if (speakerOverrides.hasRedo) {
                        speakerOverrides.redo();
                      } else {
                        videoCreator.redo();
                      }
                    }}
                    hoverColor="var(--accent)"
                    buttonSize={22}
                  />
                )}
                {ReactDOM.createPortal(
                  <Tooltip
                    id="search-tooltip"
                    style={COMPACT_TOOLTIP_STYLE}
                    place="right"
                    positionStrategy="fixed"
                  />,
                  document.body,
                )}
              </>
            )}
          </LeftSideButtons>
          <RightSideButtons>
            {originalTranscription && (
              <>
                {/* A surface with the view dropdown offers Redlines there. */}
                {!enableTranslationReviewerMode && hideCaptions && (
                  <RedlineToggleButton
                    {...redlineView}
                    onFocus={() => scrollRef.current!.focus()}
                  />
                )}
              </>
            )}
            {showQuotesToggle && (
              <HighlightTogglePill
                label="Quotes"
                ariaLabel="Pull quote marks"
                isOn={showQuotes}
                total={quoteRanges.length}
                current={activeQuoteIndex + 1}
                onToggle={handleToggleQuotes}
                onPrev={goPrevQuote}
                onNext={goNextQuote}
                prevLabel="Previous pull quote"
                nextLabel="Next pull quote"
                textColor="var(--content-quotes-text)"
                bgColor="var(--content-quotes-bg)"
                tooltipId="quotes-tooltip"
                tooltipOn="Hide pull quote marks"
                tooltipOff="Mark pull quotes"
                tooltipEmpty="No pull quotes in this story yet"
                blocked={showOnlyActive}
                onFocus={() => scrollRef.current!.focus()}
                groupTestId="transcript-quotes-group"
                buttonTestId="transcript-highlight-quotes"
              />
            )}
            {showClipsToggle && (
              <HighlightTogglePill
                label="Clips"
                ariaLabel="Clip marks"
                isOn={showClips}
                total={clipRanges.length}
                current={activeClipIndex + 1}
                onToggle={handleToggleClips}
                onPrev={goPrevClip}
                onNext={goNextClip}
                prevLabel="Previous clip"
                nextLabel="Next clip"
                textColor="var(--content-clips-text)"
                bgColor="var(--content-clips-bg)"
                tooltipId="clips-tooltip"
                tooltipOn="Hide clip marks"
                tooltipOff="Mark clips"
                tooltipEmpty="No clips in this story yet"
                blocked={showOnlyActive}
                onFocus={() => scrollRef.current!.focus()}
                groupTestId="transcript-clips-group"
                buttonTestId="transcript-highlight-clips"
              />
            )}
            {/* PLAT-11039: all four content filters sit together — Quotes,
                Clips, Highlights, then Story Topics via the filters slot.
                Accepted trade: a count appearing on one nudges its neighbors. */}
            {savedSegmentsEnabled && savedSegmentTargets.length > 0 && (
              <SavedSegmentsFilterDropdown
                segments={savedSegmentTargets}
                selectedKeys={selectedSavedSegmentKeys}
                onApply={setSelectedSavedSegmentKeys}
                hidden={savedSegmentsHidden}
                onHiddenChange={setSavedSegmentsHidden}
                mode={savedSegmentFilterMode}
                onModeChange={setSavedSegmentFilterMode}
                blockedByTopicFilter={
                  !!extraSlots.filtersOwnShowOnly || !!visibleParagraphIds
                }
                onCombine={onCombineSegment}
                onRenameSegment={(key, name) =>
                  void handleRenameSegment(key, name)
                }
                onRemoveSegment={key => void handleRemoveSegment(key)}
              />
            )}
            {resolveFilters(extraSlots.filters, {
              highlightsShowOnlyActive: savedSegmentShowOnly,
            })}
            {/* Right-pinned tail — the flexible gap before it absorbs the
                filter group's width changes. */}
            <HeaderTail>
              {extraSlots.trailing}
              <TranscriptOverflowMenu items={overflowItems} />
            </HeaderTail>
          </RightSideButtons>
        </Buttons>
      </Header>
      {/* PLAT-11039: the match navs moved into the header, so this row no
          longer appears/disappears on toggle — only topic chips render here. */}
      {belowHeader && <BelowHeaderRow>{belowHeader}</BelowHeaderRow>}
      <ScrollableText
        ref={scrollableContainerRef}
        id="transcript-scrollable-container"
        data-testid="transcript-scrollable-container"
        $topicGutter={topicGutter}
      >
        <TranscriptContent
          videoCreator={videoCreator}
          transcriptionUrl={transcriptionStore?.transcriptionUrl || ''}
          transcriptionLoadingStatus={
            transcriptionStore?.transcriptionLoadingStatus ||
            TranscriptionStatus.None
          }
          isEmpty={!transcriptElements?.length}
          isInKaraokeView={isInKaraokeView}
          showHistory={showHistory}
          cursor={cursor}
          splitTranscriptBlocks={splitTranscriptBlocks}
          diffEverMounted={diffEverMounted}
          transcriptElements={transcriptElements}
          scrollRef={scrollRef}
          setShowReplaceWarning={setShowReplaceWarning}
          onSpeakerClick={handleSpeakerClick}
          language={language}
          enableInlineSpanReplace={enableInlineSpanReplace}
          reviewHandoff={reviewHandoff}
          enableChangedHighlight={enableChangedHighlight}
          translatedCaptionLines={translatedCaptionLines}
          syntheticBreakActions={syntheticBreakActions}
          translationReviewOpen={translationReviewOpen}
          // Always feed currentTime so the view can render the paused-
          // state cursor at the upcoming phrase (PLAT-9636). The view
          // gates its visual on `isPlaying`: word-fill while playing,
          // leading-whitespace cursor while paused.
          currentTime={videoCreator.time}
          onWordClick={handleWordClick}
          clipTimelineMarkers={clipTimelineMarkers}
          onMarkerClick={onMarkerClick}
          hidePhotoHighlights={hidePhotoHighlights}
          visibleParagraphIds={effectiveVisibleParagraphIds}
          visibleElementIndices={savedSegmentVisibleElementIndices}
          highlightedParagraphIds={highlightedParagraphIds}
          paragraphHighlightColors={paragraphHighlightColors}
          activeParagraphId={activeParagraphId}
          overlayRanges={sourceOverlayRanges}
          translatedOverlayRanges={overlayRanges}
          translatedContextMenuActions={translatedContextMenuActions}
          translatedContextMenuDisabledReasons={
            translatedContextMenuDisabledReasons
          }
          onTranslatedContextMenuAction={handleTranslatedContextAction}
        />
      </ScrollableText>
      {photoSelection && (
        <Modal isOpen={true} closeModal={() => togglePhotoSelectionModal(null)}>
          <PickerModalCard>
            <PhotoModal
              TopComponent={props => (
                <PhotoModalTop
                  {...props}
                  addImageLabel="Add Image"
                  addImageLoadingLabel="Adding…"
                  replaceAction={handleAddPhotoWrapper}
                  title="Add Image"
                  isSelected={!!selectedImage}
                  selectedImage={selectedImage}
                  isLoading={false}
                  setSelectedImage={setSelectedImage}
                />
              )}
              showDescription
              otherFields={['stock', 'ai', 'org_photos']}
              onCloseSelf={() => togglePhotoSelectionModal(null)}
              selectedImageUrl={selectedImage?.url || null}
              setSelectedImage={setSelectedImage}
              origin="transcript"
            />
          </PickerModalCard>
        </Modal>
      )}
      {debugEnabled && (
        <DebugModal
          title="Transcript Debug"
          content={transcriptElements}
          onClose={() => {
            setDebugEnabled(false);
          }}
        />
      )}
      <TranscriptSpeakerContextMenu
        isOpen={!!speakerMenuInfo}
        targetElement={speakerMenuInfo?.targetElement ?? null}
        onClose={closeSpeakerMenu}
        onReassignEverywhere={handleReassignEverywhere}
        onReassignSectionOnly={handleReassignSectionOnly}
        onUnassign={handleUnassignSpeaker}
        onEdit={() =>
          speakerMenuInfo && openEditSpeaker(speakerMenuInfo.speakerLabel)
        }
        onUndoSectionReassign={handleUndoSectionReassign}
        isAnonymous={
          speakerMenuInfo
            ? resolvedSpeakerName(speakerMenuInfo.speakerLabel).startsWith(
                'Speaker ',
              )
            : false
        }
        hasSectionOverride={
          speakerMenuInfo
            ? speakerMenuInfo.sectionIndices.some(i => {
                const el = transcriptElements?.[i];
                return el ? isElementReassigned(el) : false;
              })
            : false
        }
      />
      {showReassignModal &&
        reassignSpeakerData &&
        (reassignSpeakerLabel || reassignFromSelection) && (
          <AssignSpeakerModal
            isOpen={showReassignModal}
            onClose={() => {
              closeReassignState();
              setReassignFromSelection(null);
            }}
            onAssign={handleReassignSpeaker}
            storySpeakers={reassignSpeakerData.storySpeakers}
            organizationSpeakers={reassignSpeakerData.orgSpeakers}
            speakerLabel={reassignSpeakerLabel || ''}
            speakerDisplayName={
              reassignFromSelection
                ? 'Selected Text'
                : resolvedSpeakerName(reassignSpeakerLabel!)
            }
            onCreateNewSpeaker={handleCreateNewFromTranscript}
            headerSubtitle="Reassign Speaker"
            headerTitle={
              reassignFromSelection
                ? 'Select speaker for selected text'
                : `Select new speaker for ${resolvedSpeakerName(
                    reassignSpeakerLabel!,
                  )}`
            }
          />
        )}
      {showCreateSpeakerModal && (
        <SpeakerFormModal
          isOpen={showCreateSpeakerModal}
          onClose={closeReassignState}
          onSubmit={handleCreateSpeakerFromTranscript}
        />
      )}
      {editSpeakerModal}
    </Main>
  );
});

const TranscriptContent = React.memo(
  ({
    videoCreator,
    transcriptionUrl,
    transcriptionLoadingStatus,
    isEmpty,
    isInKaraokeView,
    showHistory,
    cursor,
    splitTranscriptBlocks,
    diffEverMounted,
    transcriptElements,
    scrollRef,
    setShowReplaceWarning,
    onSpeakerClick,
    language,
    enableInlineSpanReplace,
    reviewHandoff,
    enableChangedHighlight,
    translatedCaptionLines,
    syntheticBreakActions,
    translationReviewOpen,
    currentTime,
    onWordClick,
    clipTimelineMarkers,
    onMarkerClick,
    hidePhotoHighlights,
    visibleParagraphIds,
    visibleElementIndices,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
    overlayRanges,
    translatedOverlayRanges,
    translatedContextMenuActions,
    translatedContextMenuDisabledReasons,
    onTranslatedContextMenuAction,
  }: {
    videoCreator: ITranscriptVideoCreator;
    transcriptionUrl?: string;
    transcriptionLoadingStatus: TranscriptionStatus;
    isEmpty: boolean;
    isInKaraokeView: boolean;
    showHistory: boolean;
    cursor: { from: number; to: number };
    splitTranscriptBlocks: TranscriptElementBlock[];
    diffEverMounted: boolean;
    transcriptElements: TranscriptElementWithViewData[] | undefined;
    scrollRef: React.RefObject<HTMLDivElement>;
    setShowReplaceWarning: (show: boolean) => void;
    onSpeakerClick?: (info: SpeakerClickInfo) => void;
    language: string | null;
    enableInlineSpanReplace?: boolean;
    reviewHandoff?: ReviewHandoffProp;
    enableChangedHighlight?: boolean;
    translatedCaptionLines: KaraokeLine[];
    syntheticBreakActions: {
      add: (
        lineId: string,
        phraseOriginalIndices: number[],
        textPosition: number,
      ) => void;
      remove: (breakId: string) => void;
      merge: (rightLineId: string) => void;
    } | null;
    /** PLAT-10270 (F1) — Translation Review workspace open; disables captions
     *  break-editing so its Backspace listener stays disjoint from recombine. */
    translationReviewOpen: boolean;
    currentTime: number | undefined;
    onWordClick?: (index: number) => void;
    clipTimelineMarkers?: ClipTimelineMarkers;
    onMarkerClick?: MarkerClickHandler;
    hidePhotoHighlights?: boolean;
    visibleParagraphIds?: ReadonlySet<string>;
    visibleElementIndices?: ReadonlySet<number>;
    highlightedParagraphIds?: ReadonlySet<string>;
    paragraphHighlightColors?: ReadonlyMap<string, string>;
    activeParagraphId?: string;
    overlayRanges?: HighlightRange[];
    /** Translated (phrase-lead space) overlay ranges for the translated redline
     *  view. Distinct from `overlayRanges` (source space, original redline). */
    translatedOverlayRanges?: HighlightRange[];
    /** PLAT-10646 — safe action subset for the translated prose context menu. */
    translatedContextMenuActions?: TranscriptContextMenuActions[];
    /** PLAT-11599 — which of those rows are inert on a translation, and why. */
    translatedContextMenuDisabledReasons?: TranscriptContextMenuDisabledReasons;
    /** PLAT-10646 — dispatch a translated context-menu action with its mapped
     *  source element range. */
    onTranslatedContextMenuAction?: (
      action: TranscriptContextMenuActions,
      range: { startIndex: number; endIndex: number },
    ) => void;
  }) => {
    if (
      videoCreator.storyId &&
      !transcriptionUrl &&
      transcriptionLoadingStatus !== TranscriptionStatus.Failed &&
      transcriptionLoadingStatus !== TranscriptionStatus.GenerationFailed
    ) {
      return (
        <div>
          <div style={{ width: '100%' }}>Transcript is regenerating...</div>
          {transcriptionLoadingStatus === 'none' && (
            <div>
              Click{' '}
              <PanelButton onClick={() => setShowReplaceWarning(true)}>
                <ReplaceIcon />
              </PanelButton>{' '}
              to Re-generate again
            </div>
          )}
        </div>
      );
    }

    if (
      !transcriptionUrl &&
      (transcriptionLoadingStatus === TranscriptionStatus.Failed ||
        transcriptionLoadingStatus === TranscriptionStatus.GenerationFailed)
    ) {
      return (
        <div>
          <div style={{ width: '100%' }}>
            Failed to generate the transcript.
          </div>
          <div>
            Click{' '}
            <PanelButton onClick={() => setShowReplaceWarning(true)}>
              <ReplaceIcon />
            </PanelButton>{' '}
            to try again
          </div>
          <div>Or contact developers about the issue</div>
        </div>
      );
    }
    if (
      transcriptionUrl &&
      transcriptionLoadingStatus === TranscriptionStatus.Loading
    ) {
      return <div>Transcription is loading...</div>;
    }
    if (
      transcriptionUrl &&
      transcriptionLoadingStatus === TranscriptionStatus.Failed
    ) {
      return <div>Transcription loading failed. Try refreshing the page.</div>;
    }
    if (transcriptionLoadingStatus === TranscriptionStatus.Failed && isEmpty) {
      return <div>Transcription loading failed. Try refreshing the page.</div>;
    }
    // Seek handler shared between the clip-mode translated view and the
    // redline translated view. Seeks the playhead to the clicked phrase's
    // first source word. `useTranslatedElements` renders the translated
    // value on the phrase-lead only, so the incoming `initialIndex` is
    // the start of the spoken phrase — matching the un-translated
    // transcript's click-to-seek behavior.
    const seekToIndex = (initialIndex: number) => {
      seekToInitialIndex(videoCreator, transcriptElements, initialIndex);
    };

    // Translated redline view: "See full transcript" + translation
    // language. Parallel to `TranscriptionDiffNew` (the original-language
    // redline). Renders all phrases including cut/removed with
    // strike-through styling derived from the worst state across the
    // phrase's source members.
    if (showHistory && language && transcriptElements) {
      return (
        <TranscriptionDiffTranslated
          language={language}
          transcriptElements={transcriptElements}
          currentTime={currentTime}
          isPlaying={videoCreator.isPlaying}
          onWordClick={seekToIndex}
          visibleParagraphIds={visibleParagraphIds}
          highlightedParagraphIds={highlightedParagraphIds}
          paragraphHighlightColors={paragraphHighlightColors}
          activeParagraphId={activeParagraphId}
          overlayRanges={translatedOverlayRanges}
        />
      );
    }
    // PLAT-9621: CAPTIONS toggle wins over the language-based translated
    // transcript projection — the user wants the line-by-line caption
    // breakdown, not the paragraph view. `translatedCaptionLines` (PLAT-9626
    // hook) handles language resolution; empty result means "no translated
    // captions to show" → original-language KaraokeLinesView.
    if (isInKaraokeView && transcriptElements) {
      if (translatedCaptionLines.length > 0) {
        return (
          <TranslatedCaptionsView
            lines={translatedCaptionLines}
            currentTime={currentTime}
            isPlaying={videoCreator.isPlaying}
            onLineClick={seekToIndex}
            // PLAT-10457 (A3): mirror the prose translated branch — forward the
            // Fix-Translation enable flag so the view stamps source-phrase
            // identity on its word spans (the A4/A5 gesture resolves clicks /
            // selections off those attrs).
            enableInlineSpanReplace={enableInlineSpanReplace}
            // PLAT-10457 (A8): forward the changed-phrase highlight flag so an
            // EDITED caption phrase shows the same green "changed" background as
            // the prose branch (see the `TranscriptionTextTranslated` mount
            // below). Closes the A3-deferred forwarding — the highlight consumer
            // landed after the A4/A5 gesture.
            enableChangedHighlight={enableChangedHighlight}
            // PLAT-10457 (A4): the mounted InlineSpanReplaceLayer authors the
            // override against the active translation language. This view had no
            // `language` prop before A4; thread it here, mirroring the A3 flag.
            language={language}
            // PLAT-9623: as the user arrows through word boundaries
            // in the captions panel, seek the video to the matching
            // synthetic time (computed via the same SplitStrategy
            // used for word placement / synthetic break splits) so
            // the preview frame follows the cursor.
            onSeekToTime={time => {
              dispatchTranscriptSeek(time, videoCreator);
              void videoCreator.setTime(time, true);
            }}
            // PLAT-10270 (F1): undefined while the Translation Review workspace
            // is open, which disables break-editing so the captions window
            // Backspace listener never registers and stays disjoint from the
            // review recombine gesture (SPEC criterion 5). See
            // `resolveCaptionBreakHandlers`.
            syntheticBreakHandlers={resolveCaptionBreakHandlers(
              syntheticBreakActions,
              translationReviewOpen,
            )}
          />
        );
      }
      return (
        <KaraokeLinesView
          cursor={cursor}
          transcriptElements={transcriptElements}
          isPlaying={videoCreator.isPlaying}
          setActiveElement={id => videoCreator.setActiveElements(id)}
        />
      );
    }
    // Translated view (regular, non-redline). Parallel to
    // `TranscriptionText` (the original-language regular view). Takes the
    // same clip-aware `transcriptElements` array, derives speaker headers
    // / paragraph breaks / timestamps from element metadata, and renders
    // translation phrase values at phrase-lead positions.
    if (language && transcriptElements) {
      return (
        <TranscriptionTextTranslated
          language={language}
          transcriptElements={transcriptElements}
          currentTime={currentTime}
          isPlaying={videoCreator.isPlaying}
          onWordClick={seekToIndex}
          onSpeakerClick={onSpeakerClick}
          visibleParagraphIds={visibleParagraphIds}
          highlightedParagraphIds={highlightedParagraphIds}
          paragraphHighlightColors={paragraphHighlightColors}
          activeParagraphId={activeParagraphId}
          overlayRanges={translatedOverlayRanges}
          enableInlineSpanReplace={enableInlineSpanReplace}
          reviewHandoff={reviewHandoff}
          enableChangedHighlight={enableChangedHighlight}
          contextMenuActions={translatedContextMenuActions}
          contextMenuDisabledReasons={translatedContextMenuDisabledReasons}
          onContextMenuAction={onTranslatedContextMenuAction}
        />
      );
    } else {
      // PLAT-9646: keep BOTH views mounted, toggling visibility via CSS.
      // Mirrors PLAT-9408's diff-keepalive optimization for the text view —
      // exiting redline used to freshly remount TranscriptionText
      // (thousands of word spans), making the toggle feel as slow as the
      // first redline mount. Both views in stable JSX positions so React
      // never unmounts; auto-scroll's querySelector handles the duplicate
      // `data-index` collision by skipping zero-rect (display:none) matches.
      return (
        <>
          <div style={{ display: showHistory ? 'none' : 'contents' }}>
            <TranscriptionText
              cursor={cursor}
              transcriptElements={transcriptElements}
              scrollRef={scrollRef}
              onSpeakerClick={onSpeakerClick}
              timelineMarkers={clipTimelineMarkers}
              onMarkerClick={onMarkerClick}
              hidePhotoHighlights={hidePhotoHighlights}
              isPlaying={videoCreator.isPlaying}
              punchListSignature={
                videoCreator.punchListManager.punchListSignature
              }
              getPhotoHighlightViewData={i =>
                videoCreator.punchListManager.getPhotoHighlightViewData(i)
              }
              visibleParagraphIds={visibleParagraphIds}
              visibleElementIndices={visibleElementIndices}
              highlightedParagraphIds={highlightedParagraphIds}
              paragraphHighlightColors={paragraphHighlightColors}
              activeParagraphId={activeParagraphId}
            />
          </div>
          {diffEverMounted && (
            <div style={{ display: showHistory ? 'contents' : 'none' }}>
              <TranscriptionDiffNew
                cursor={cursor}
                blocks={splitTranscriptBlocks}
                onSpeakerClick={onSpeakerClick}
                onWordClick={onWordClick}
                isVisible={showHistory}
                visibleParagraphIds={visibleParagraphIds}
                highlightedParagraphIds={highlightedParagraphIds}
                paragraphHighlightColors={paragraphHighlightColors}
                activeParagraphId={activeParagraphId}
                overlayRanges={overlayRanges}
              />
            </div>
          )}
        </>
      );
    }
  },
);

const Main = styled.div<{ $compact?: boolean }>`
  height: calc(100% - 16px);
  box-sizing: border-box;
  width: calc(100% - 32px);
  font-size: var(--text-sm);
  margin-left: 32px;
  overflow: clip;
  padding-top: ${props => (props.$compact ? '0' : '12px')};
  display: flex;
  flex-direction: column;
  &:focus {
    outline: none;
  }

  ${forIPad(`
    margin-left: 8px;
    width: calc(100% - 8px);
  `)}
`;

const BelowHeaderRow = styled.div`
  flex-shrink: 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const SearchAnchor = styled.div`
  position: relative;
`;

const FloatingSearch = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  z-index: 100001;
`;

const Buttons = styled.div`
  height: 26px;
  align-items: center;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  /* PLAT-11039: fill the header so HeaderTail pins to the pane's right edge,
     leaving a gap that absorbs the Quotes/Clips pill expansion. */
  flex-grow: 1;
  padding-right: 2px;
`;

const RightSideButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 100%;
  flex-shrink: 0;
  /* PLAT-11039: grow so HeaderTail's margin-left: auto has a gap to pin to. */
  flex-grow: 1;
`;

const LeftSideButtons = styled(RightSideButtons)`
  gap: 8px;
  flex-grow: 0;
`;
